<template>
  <div id="app" :class="['business-layout', { 'mobile-mode': isMobile }]">
    <!-- 導覽側邊欄 -->
    <aside class="sidebar">
      <div class="brand brand-clickable" @click="goToHome"><div class="logo-box">H</div><div class="brand-name">房產動態</div></div>
      <div class="city-switcher">
        <select class="city-select" v-model="currentCity" @change="switchCity(currentCity)">
          <option v-for="c in cities" :key="c.id" :value="c.id" :disabled="c.disabled">{{ c.name }}{{ c.disabled ? ' (即將開放)' : '' }}</option>
        </select>
      </div>
      <nav class="sidebar-nav">
        <button :class="{ active: currentView === 'dashboard' }" @click="goToDashboard" class="nav-btn">市場動能監控</button>
        <button :class="{ active: currentView === 'list' }" @click="goToList" class="nav-btn">全市物件清單</button>
        <button :class="{ active: currentView === 'insights' }" @click="goToInsights" class="nav-btn">市場洞察分析</button>
      </nav>
      <div class="sidebar-footer">
        <p class="last-sync">資料載入: {{ lastSyncTime }}</p>
      </div>
    </aside>

    <!-- 手機端底部導覽 -->
    <nav class="mobile-nav">
      <button :class="{ active: currentView === 'dashboard' }" @click="goToDashboard" class="m-nav-btn">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
        <span>市場監控</span>
      </button>
      <button :class="{ active: currentView === 'list' }" @click="goToList" class="m-nav-btn">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
        <span>物件清單</span>
      </button>
      <button :class="{ active: currentView === 'insights' }" @click="goToInsights" class="m-nav-btn">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        <span>市場洞察</span>
      </button>
    </nav>

    <main class="main-content">
      <header class="top-bar" v-if="currentView !== 'home'">
        <div class="title-section">
          <div class="main-title-row">
            <h1>{{ viewTitle }}</h1>
            <div class="title-desc">
              <p class="subtitle">{{ viewSubtitle }}</p>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <div class="district-selector">
            <span class="selector-label" v-if="!isMobile">快速區域切換：</span>
            <select v-model="currentDistrict" class="custom-select" @change="onDistrictChange">
              <option value="All">全部行政區</option>
              <option v-for="d in uniqueDistricts" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
          <button v-if="currentDistrict !== 'All'" class="btn-clear-filter" @click="selectDistrict('All')">清除篩選</button>
        </div>
      </header>

    <!-- Mobile Header -->
    <header class="mobile-top-header" v-if="isMobile">
      <div class="mob-city-switcher">
        <button v-for="c in cities" :key="c.id" 
                :class="['mob-city-btn', {active: currentCity === c.id, disabled: c.disabled}]" 
                :disabled="c.disabled"
                @click="switchCity(c.id)">
          {{ c.short }}
        </button>
      </div>
    </header>

      <!-- A. Landing Page -->
      <section v-if="currentView === 'home'" class="landing-page">
        <div class="landing-content">
          <div class="landing-hero">
            <h1 class="landing-title">HouseTrend</h1>
            <p class="landing-tagline">房市大數據監控系統</p>
          </div>

          <div class="g-search-wrap">
            <div class="g-search-bar">
              <svg class="g-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="貼上信義房屋物件連結，或輸入社區／行政區名稱"
                @keyup.enter="parseAndGo"
              >
              <button class="g-search-submit" @click="parseAndGo">搜尋</button>
            </div>
            <div class="g-platform-row">
              <span class="g-platform-badge">信義房屋</span>
            </div>
          </div>

          <div class="g-action-row">
            <button class="g-btn-secondary" @click="goToDashboard">進入儀表板 →</button>
          </div>
        </div>
      </section>

      <!-- B. 市場概況 (Dashboard) -->
      <section v-if="currentView === 'dashboard'" class="dashboard-grid">
        <div class="stats-row">
          <div class="stat-card blue-theme">
            <div class="stat-label"><span class="info-tip" data-tip="目前系統監控中的有效掛牌總數">市場活躍度</span></div>
            <div class="stat-value"><div class="main-num">{{ pagination.total }} <span class="unit">件</span></div><div class="sub-info"><span class="info-val">+{{ newCount }}</span> <span class="info-label">較昨日新增</span></div></div>
          </div>
          <div class="stat-card red-theme highlight">
            <div class="stat-label"><span class="info-tip" data-tip="今日發生價格向下調整的物件總數與比例">降價動能</span></div>
            <div class="stat-value"><div class="main-num">{{ dropCount }} <span class="unit">件</span></div><div class="sub-info"><span class="info-val">{{ dropPercent }}%</span> <span class="info-label">今日降價率</span></div></div>
            <div class="visual-bar"><div class="bar-fill" :style="{ width: dropPercent + '%' }"></div></div>
          </div>
          <div class="stat-card purple-theme">
            <div class="stat-label"><span class="info-tip" data-tip="該區域內所有物件的每坪單價平均值">區域平均單價</span></div>
            <div class="stat-value">
              <div class="main-num">{{ avgUniPrice }} <span class="unit">萬/坪</span></div>
              <div class="sub-info"><span :class="['info-val', priceChangeRate >= 0 ? 'text-purple' : 'text-red']">{{ priceChangeRate > 0 ? '+' : '' }}{{ priceChangeRate }}%</span> <span class="info-label">總體波幅</span></div>
            </div>
            <div class="visual-bar"><div class="bar-fill" :style="{ width: Math.min(Math.abs(priceChangeRate)*10, 100) + '%' }"></div></div>
          </div>
          <div class="stat-card teal-theme">
            <div class="stat-label"><span class="info-tip" data-tip="該區域內掛牌物件的平均成交期望總額">區域平均總價</span></div>
            <div class="stat-value">
              <div class="main-num">{{ avgTotalPrice }} <span class="unit">萬</span></div>
              <div class="sub-info"><span class="info-val">{{ marketStability }}</span> <span class="info-label">行情分析</span></div>
            </div>
            <div class="visual-bar"><div class="bar-fill" style="width: 75%"></div></div>
          </div>
        </div>

        <div class="visual-row">
          <div class="main-visual card"><div class="card-header"><h3>市場價格波動趨勢 (30日)</h3></div><div class="chart-box"><canvas id="marketTrendChart"></canvas></div></div>
          <div class="side-visual card no-padding"><div class="card-header compact"><h3>{{ currentDistrict === 'All' ? '行政區物件佔比' : '區域價格分佈' }}</h3></div><div class="chart-box"><canvas id="pieChart"></canvas></div></div>
        </div>

        <div class="eye-catching-section" v-if="eyeCatchingList.length">
          <div class="section-header"><h3>精選必看物件 (急售 / 大幅修正)</h3><span class="algo-tip">依降幅、鮮度與金額綜合評分</span></div>
          <div class="eye-catching-grid">
            <div v-for="item in eyeCatchingList" :key="item.houseNo" class="hot-card" @click="showDetail(item)">
              <div class="hot-tag">重點推薦</div>
              <div class="hot-content">
                <div class="hot-name">{{ item.commName || item.name }}</div>
                <div class="hot-district">{{ extractDistrict(item.address) }}</div>
                <div class="hot-price"><span class="price">{{ formatMoney(item.totalPrice) }}</span><span v-if="isLocked(item)" class="lock-pill">🔒 降幅</span><span v-else class="drop-pill">-{{ item.dropPct }}%</span></div>
                <div class="hot-save" v-if="isLocked(item)">解鎖查看降價金額</div>
                <div class="hot-save" v-else>現省 {{ Math.abs(item.totalPrice - item.lastPrice) }} 萬</div>
              </div>
              <div class="hot-footer">{{ getDayLabel(item.updatedDate) === '今日' ? '今日更新' : getDayLabel(item.updatedDate) + '前更新' }}</div>
            </div>
          </div>
        </div>

        <div class="eye-catching-section" v-if="showcaseItems.length">
          <div class="section-header"><h3>今日免費解鎖：完整降價時間軸</h3><span class="algo-tip">每天換一批多次降價物件</span></div>
          <div class="eye-catching-grid">
            <div v-for="item in showcaseItems" :key="item.houseNo" class="hot-card" @click="showDetail(item)">
              <div class="hot-tag showcase-tag">今日免費</div>
              <div class="hot-content">
                <div class="hot-name">{{ item.commName || item.name }}</div>
                <div class="hot-district">{{ extractDistrict(item.address) }}</div>
                <div class="hot-price"><span class="price">{{ formatMoney(item.totalPrice) }}</span><span class="drop-pill">降 {{ item.changeCount }} 次</span></div>
                <div class="hot-save" v-if="item.priceFirst > item.totalPrice">累計 -{{ item.priceFirst - item.totalPrice }} 萬</div>
              </div>
              <div class="hot-footer">點擊看每一次怎麼降</div>
            </div>
          </div>
        </div>

        <div id="relist-section" class="eye-catching-section" v-if="relistItems.length">
          <div class="section-header"><h3>近 30 天換編號重新上架</h3><span class="algo-tip">下架後以新編號刊登，信義上的降價紀錄已歸零</span></div>
          <div class="eye-catching-grid">
            <div v-for="item in relistItems" :key="item.houseNo" class="hot-card" @click="showDetail(item)">
              <div class="hot-tag relist-tag">重新上架</div>
              <div class="hot-content">
                <div class="hot-name">{{ item.commName || item.name }}</div>
                <div class="hot-district">{{ extractDistrict(item.address) }} · 隔 {{ item.relist.gapDays }} 天</div>
                <div class="hot-price"><span class="price">{{ formatMoney(item.totalPrice) }}</span>
                  <span v-if="item.relist.prevPrice == null" class="lock-pill">🔒 前次價格</span>
                  <span v-else-if="item.relist.firstPrice < item.relist.prevPrice" class="drop-pill">-{{ item.relist.prevPrice - item.relist.firstPrice }} 萬</span>
                </div>
                <div class="hot-save" v-if="item.relist.prevPrice != null">前次刊登最後 {{ formatMoney(item.relist.prevPrice) }}</div>
              </div>
              <div class="hot-footer">點擊看前次刊登</div>
            </div>
          </div>
        </div>

        <div class="analysis-grid secondary">
          <div class="chart-card card">
            <div class="card-header"><h3>累計降價排行 (前10)</h3><div class="chart-legend mini"><span class="dot green"></span>現價 <span class="dot red"></span>累計降幅</div></div>
            <div class="chart-box small"><canvas id="barChart"></canvas></div>
          </div>
          <div class="chart-card card">
            <div class="card-header"><h3>累計漲價排行 (前10)</h3><div class="chart-legend mini"><span class="dot purple"></span>初始開價 <span class="dot blue"></span>累計漲幅</div></div>
            <div class="chart-box small"><canvas id="riseBarChart"></canvas></div>
          </div>
        </div>

        <div class="analysis-grid single">
          <div class="chart-card card"><div class="card-header"><h3>價格區間量能與市場修正強度</h3></div><div class="chart-box small"><canvas id="priceRangeChart"></canvas></div></div>
        </div>

        <div class="table-card card">
          <div class="card-header"><h3>近期價格異動物件</h3><button class="btn-outline btn-sm" @click="goToList">查看完整清單</button></div>
          <div class="table-wrapper">
            <table class="premium-table">
              <thead><tr><th v-if="!isMobile">區域</th><th>物件詳細資訊</th><th>價格動態</th><th>更新</th></tr></thead>
              <tbody>
                <tr v-for="item in highlights.recentChanges" :key="item.houseNo" class="clickable-row" @click="showDetail(item)">
                  <td v-if="!isMobile"><span class="badge-btn" @click.stop="selectDistrict(extractDistrict(item.address))">{{ extractDistrict(item.address) }}</span></td>
                  <td><div class="property-cell"><div class="name name-link">{{ item.commName || item.name }} <span class="name-arrow">→</span></div><div class="meta">{{ item.layout }} · {{ item.areaBuilding }}坪</div></div></td>
                  <td class="price-cell">
                    <div class="price-flow">
                      <span v-if="item.totalPrice < item.lastPrice" class="old-price"><s>{{ item.lastPrice }}</s></span>
                      <span class="current-price">{{ formatMoney(item.totalPrice) }}</span>
                      <span v-if="item.totalPrice < item.lastPrice" class="trend-tag">-{{ Math.abs(item.totalPrice - item.lastPrice) }}萬</span>
                      <span v-if="item.totalPrice > item.lastPrice" class="trend-tag rise-tag">+{{ Math.abs(item.totalPrice - item.lastPrice) }}萬</span>
                      <span v-if="isLocked(item)" class="lock-pill">🔒</span>
                    </div>
                  </td>
                  <td><span :class="['day-badge', getDayColor(item.updatedDate)]">{{ getDayLabel(item.updatedDate) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-else-if="currentView === 'list'" class="list-container">
        <div class="filter-bar card">
          <div class="filter-group"><label>行政區域</label><select v-model="currentDistrict" class="custom-select"><option value="All">全部行政區</option><option v-for="d in uniqueDistricts" :key="d" :value="d">{{ d }}</option></select></div>
          <div class="filter-group"><label>排序邏輯</label><select v-model="filters.sortBy" class="custom-select"><option value="dateDesc">最新更新</option><option value="priceAsc">價格由低到高</option><option value="priceDesc">價格由高到低</option><option value="dropCountDesc">降價次數最多</option></select></div>
          <div class="filter-group search-group"><label>關鍵字搜尋</label><input type="text" v-model="searchQuery" placeholder="搜尋建案名稱或地址..." class="search-box"></div>
        </div>
        <div class="card"><div class="table-wrapper">
          <table class="premium-table">
            <thead><tr><th>區域</th><th>物件名稱</th><th>當前價格</th><th>降價次數</th></tr></thead>
            <tbody>
              <tr v-for="item in searchedList" :key="item.houseNo" class="clickable-row" @click="showDetail(item)">
                <td><span class="badge-btn" @click.stop="selectDistrict(extractDistrict(item.address))">{{ extractDistrict(item.address) }}</span></td>
                <td><div class="property-cell"><div class="name name-link">{{ item.commName || item.name }} <span class="name-arrow">→</span></div><div class="meta">{{ item.address }}</div></div></td>
                <td class="price-cell"><span class="current-price">{{ formatMoney(item.totalPrice) }}</span></td>
                <td><span class="drop-pill">{{ item.changeCount || 0 }} 次</span><span v-if="isLocked(item) && item.changeCount" class="lock-pill lock-pill-inline">🔒</span></td>
              </tr>
            </tbody>
          </table>
          <div v-if="pagination.hasMore" style="padding: 20px; text-align: center;">
            <button @click="loadMore" :disabled="pagination.loading" class="btn-outline" style="width: 100%; padding: 12px; cursor: pointer; border: 1px solid var(--border); border-radius: 8px; background: transparent; color: var(--text-main);">
              {{ pagination.loading ? '正在載入...' : '載入更多物件 (還有 ' + (pagination.total - pagination.items.length) + ' 筆)' }}
            </button>
          </div>
          <div v-else-if="pagination.total > 0" style="padding: 20px; text-align: center; color: var(--text-dim); font-size: 0.9rem;">已顯示所有物件</div>
        </div></div>
      </section>

      <!-- 市場洞察分析頁面 -->
      <section v-else-if="currentView === 'insights'" class="insights-container">

        <!-- A. 掛牌壽命 -->
        <div class="section-header">
          <h3>掛牌壽命分析</h3>
          <span class="algo-tip">排除開始追蹤前就已上架的物件（真正上架日未知）<template v-if="listingAge">，樣本 {{ listingAge.sample }} / {{ listingAge.total }} 件</template></span>
        </div>
        <div class="stats-row" style="margin-bottom:2rem">
          <div class="stat-card blue-theme">
            <div class="stat-label">平均掛牌天數</div>
            <div class="stat-value"><div class="main-num">{{ avgListingDays }}<span class="unit"> 天</span></div></div>
          </div>
          <div class="stat-card" style="--theme-color: var(--orange)">
            <div class="stat-label">掛牌 30 天以上</div>
            <div class="stat-value"><div class="main-num">{{ longListedCount }}<span class="unit"> 件</span></div><div class="sub-info"><span class="info-val" style="color:var(--orange)">{{ longListedPct }}%</span></div></div>
          </div>
          <div class="stat-card red-theme">
            <div class="stat-label">掛牌 90 天以上</div>
            <div class="stat-value"><div class="main-num">{{ veryLongListedCount }}<span class="unit"> 件</span></div><div class="sub-info"><span class="info-val">{{ veryLongListedPct }}%</span></div></div>
          </div>
        </div>
        <div class="chart-card card" style="margin-bottom:3rem">
          <div class="card-header"><h3>掛牌天數分佈</h3></div>
          <div class="chart-box small"><canvas id="ageDistChart"></canvas></div>
        </div>

        <!-- B2. 降價後久未售出 -->
        <div id="stale-section" class="section-header" style="margin-top:1rem" v-if="staleData">
          <h3>降價後久未售出</h3>
          <span class="algo-tip">已降過價，但最後一次降價距今超過 {{ staleData.minDays }} 天仍在架</span>
        </div>
        <div class="chart-row two-col" style="margin-bottom:3rem" v-if="staleData">
          <div class="card">
            <div class="card-header"><h3>共 {{ staleData.total }} 件 · 各區數量</h3></div>
            <div class="table-wrapper">
              <table class="premium-table">
                <thead><tr><th>行政區</th><th>件數</th></tr></thead>
                <tbody>
                  <tr v-for="d in staleData.byDistrict.slice(0, 10)" :key="d.district"><td>{{ d.district }}</td><td>{{ d.count }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="card">
            <div class="card-header"><h3>清單（降價次數多、擱置久的優先）</h3></div>
            <div v-if="staleData.locked" class="stale-locked">
              <p>這份清單只有長期追蹤才算得出來：哪些屋主降過價、卻已經好幾個月沒再動。</p>
              <button class="btn-primary" @click="openPaywall(null, 'stale')">🔒 解鎖 {{ staleData.total }} 件清單</button>
            </div>
            <div v-else class="table-wrapper stale-scroll">
              <table class="premium-table">
                <thead><tr><th>物件</th><th>現價</th><th>降價</th><th>距上次降價</th></tr></thead>
                <tbody>
                  <tr v-for="item in staleData.items.slice(0, 30)" :key="item.houseNo" class="clickable-row" @click="showDetailByHouseNo(item.houseNo)">
                    <td><div class="property-cell"><div class="name name-link">{{ item.commName || item.name || '-' }} <span class="name-arrow">→</span></div><div class="meta">{{ extractDistrict(item.address) }}</div></div></td>
                    <td>{{ formatMoney(item.totalPrice) }}</td>
                    <td><span class="drop-pill">{{ item.dropCount }} 次</span><span v-if="item.totalDrop > 0" class="text-red"> -{{ item.totalDrop }} 萬</span></td>
                    <td>{{ item.daysSinceDrop }} 天</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- B3. 降價後下架 -->
        <div class="section-header" style="margin-top:1rem" v-if="dropExit && dropExit.delistedAfterDrop">
          <h3>降價後下架</h3>
          <span class="algo-tip">近 180 天曾降價、之後下架的物件；下架不等於成交，也可能是屋主撤委託</span>
        </div>
        <div class="stats-row" style="margin-bottom:3rem" v-if="dropExit && dropExit.delistedAfterDrop">
          <div class="stat-card blue-theme">
            <div class="stat-label">降價後 30 天內下架</div>
            <div class="stat-value"><div class="main-num">{{ dropExit.within30 }}<span class="unit"> 件</span></div><div class="sub-info"><span class="info-val">{{ dropExit.within30Pct }}%</span></div></div>
          </div>
          <div class="stat-card" style="--theme-color: var(--orange)">
            <div class="stat-label">最後降價到下架（中位數）</div>
            <div class="stat-value"><div class="main-num">{{ dropExit.medianDays }}<span class="unit"> 天</span></div></div>
          </div>
          <div class="stat-card red-theme">
            <div class="stat-label">下架前累計降幅（平均）</div>
            <div class="stat-value"><div class="main-num">{{ dropExit.avgDropPctBeforeExit != null ? dropExit.avgDropPctBeforeExit : '-' }}<span class="unit"> %</span></div></div>
          </div>
        </div>

        <!-- B. 降價行為 -->
        <div class="section-header" style="margin-top:1rem">
          <h3>降價行為分析</h3>
        </div>
        <div class="stats-row" style="margin-bottom:2rem">
          <div class="stat-card blue-theme">
            <div class="stat-label">平均降價次數</div>
            <div class="stat-value"><div class="main-num">{{ avgDropCount }}<span class="unit"> 次</span></div></div>
          </div>
          <div class="stat-card red-theme">
            <div class="stat-label">平均累積降幅</div>
            <div class="stat-value"><div class="main-num">{{ avgTotalDropAmt }}<span class="unit"> 萬</span></div></div>
          </div>
          <div class="stat-card purple-theme">
            <div class="stat-label">平均降價 %</div>
            <div class="stat-value"><div class="main-num">{{ avgDropPct }}<span class="unit"> %</span></div></div>
          </div>
        </div>
        <div class="chart-row two-col" style="margin-bottom:2rem">
          <div class="chart-card card">
            <div class="card-header"><h3>降價百分比分佈</h3></div>
            <div class="chart-box small"><canvas id="dropPctDistChart"></canvas></div>
          </div>
          <div class="chart-card card">
            <div class="card-header"><h3>降價金額分佈 (萬)</h3></div>
            <div class="chart-box small"><canvas id="dropAmtDistChart"></canvas></div>
          </div>
        </div>
        <div class="card" style="margin-bottom:3rem">
          <div class="card-header"><h3>降價次數排行 (前10)</h3></div>
          <div class="table-wrapper">
            <table class="premium-table">
              <thead><tr><th>物件</th><th>降價次數</th><th>累計降幅</th><th>降幅 %</th></tr></thead>
              <tbody>
                <tr v-for="item in topDropBehavior" :key="item.houseNo" class="clickable-row" @click="showDetailByHouseNo(item.houseNo)">
                  <td><div class="property-cell"><div class="name name-link">{{ item.commName || item.name || '-' }} <span class="name-arrow">→</span></div><div class="meta">{{ extractDistrict(item.address) }}</div></div></td>
                  <td><span class="drop-pill">{{ item.changeCount }} 次</span></td>
                  <td v-if="isLocked(item)" class="text-dim lock-cell" @click.stop="openPaywall(item)">🔒</td>
                  <td v-else class="text-red">{{ item.totalDrop > 0 ? '-' + item.totalDrop + ' 萬' : '-' }}</td>
                  <td v-if="isLocked(item)" class="text-dim lock-cell" @click.stop="openPaywall(item)">🔒</td>
                  <td v-else class="text-red">{{ item.dropPct > 0 ? item.dropPct + '%' : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- C. 社區熱度 -->
        <div class="section-header" style="margin-top:1rem">
          <h3>社區熱度排行</h3>
        </div>
        <div class="card" style="margin-bottom:3rem">
          <div class="table-wrapper">
            <table class="premium-table">
              <thead><tr><th>社區名稱</th><th>掛牌數</th><th>降價件數</th><th>降價率</th><th>平均降幅</th></tr></thead>
              <tbody>
                <tr v-for="c in communityHeatData" :key="c.commName" class="clickable-row" @click="goToListByCommunity(c.commName)">
                  <td><span class="name name-link">{{ c.commName }} <span class="name-arrow">→</span></span></td>
                  <td>{{ c.listingCount }}</td>
                  <td>{{ c.dropCount }}</td>
                  <td><span :class="(c.dropCount / c.listingCount) > 0.5 ? 'text-red' : 'text-dim'">{{ ((c.dropCount / c.listingCount) * 100).toFixed(0) }}%</span></td>
                  <td v-if="c.visibility === 'LOCKED'" class="text-dim">🔒</td>
                  <td v-else>{{ c.avgDropAmt ? Math.round(c.avgDropAmt) + ' 萬' : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- E. 區域對比矩陣 -->
        <div class="section-header" style="margin-top:1rem">
          <h3>區域對比矩陣</h3>
          <span class="algo-tip">點擊欄位標題排序</span>
        </div>
        <div class="card">
          <div class="table-wrapper">
            <table class="premium-table matrix-table">
              <thead>
                <tr>
                  <th>行政區</th>
                  <th class="sortable" @click="matrixSort='count'" :class="{active: matrixSort==='count'}">物件數 ↕</th>
                  <th class="sortable" @click="matrixSort='avgPrice'" :class="{active: matrixSort==='avgPrice'}">均價(萬) ↕</th>
                  <th class="sortable" @click="matrixSort='dropRate'" :class="{active: matrixSort==='dropRate'}">降價率 ↕</th>
                  <th class="sortable" @click="matrixSort='avgDays'" :class="{active: matrixSort==='avgDays'}">平均掛牌天數 ↕</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in districtMatrix.slice().sort((a,b) => (b[matrixSort]||0) - (a[matrixSort]||0))" :key="d.district" class="clickable-row" @click="selectDistrict(d.district)">
                  <td><span class="badge-btn">{{ d.district }}</span></td>
                  <td>{{ d.count }}</td>
                  <td>{{ d.avgPrice }}</td>
                  <td><span :class="d.dropRate >= 50 ? 'text-red' : d.dropRate >= 30 ? 'text-orange' : 'text-green'">{{ d.dropRate }}%</span></td>
                  <td><span :class="d.avgDays >= 60 ? 'text-red' : d.avgDays >= 30 ? 'text-orange' : 'text-dim'">{{ d.avgDays !== null ? d.avgDays + ' 天' : '-' }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </section>

      <!-- 物件詳情分析頁面 -->
      <section v-else-if="currentView === 'detail'" class="detail-container">
        <nav v-if="exploreLoc" class="breadcrumb" data-module="breadcrumb">
          <a @click="goCityOf('breadcrumb')">{{ cityNameOf(exploreLoc.city) }}</a>
          <template v-if="exploreLoc.district"><span class="crumb-sep">›</span><a @click="goDistrictOf('breadcrumb')">{{ exploreLoc.district }}</a></template>
          <template v-if="exploreLoc.commName"><span class="crumb-sep">›</span><a @click="goCommunityOf('breadcrumb')">{{ exploreLoc.commName }}</a></template>
          <template v-else-if="exploreLoc.road"><span class="crumb-sep">›</span><a @click="goRoadOf('breadcrumb')">{{ exploreLoc.road }}</a></template>
          <span class="crumb-sep">›</span><span class="crumb-current">{{ selectedProperty.commName || selectedProperty.name }}</span>
        </nav>
        <div class="detail-header card">
          <div class="back-row">
            <button v-if="detailFromInside" class="btn-outline btn-sm" @click="goBack">← 返回</button>
            <button v-else-if="exploreLoc && exploreLoc.district" class="btn-outline btn-sm" @click="goDistrictOf('back')">看{{ exploreLoc.district }}其他物件 →</button>
            <button v-else class="btn-outline btn-sm" @click="goToList">← 返回列表</button>
          </div>
          <div v-if="explore && explore.successor" class="successor-banner">
            <span>🔁 此物件已於約 {{ explore.successor.firstSeen.slice(0, 10) }} 以新編號重新刊登</span>
            <button class="btn-primary btn-sm" @click="openRelated(explore.successor.houseNo, 'relist', 0)">看新刊登 →</button>
          </div>
          <div class="detail-main-info">
            <div class="info-left">
              <h1>{{ selectedProperty.commName || selectedProperty.name }}</h1>
              <p class="detail-addr">{{ selectedProperty.address }}</p>
              <p v-if="statusLine" class="status-line" :class="{ 'status-unlisted': isUnlisted }">{{ statusLine }}</p>
              <div v-if="exploreLoc" class="loc-chips">
                <a v-if="exploreLoc.district" class="loc-chip" @click="goDistrictOf('chip')">📍 {{ exploreLoc.district }}</a>
                <a v-if="exploreLoc.road" class="loc-chip" @click="goRoadOf('chip')">{{ exploreLoc.road }}</a>
                <a v-if="exploreLoc.commName" class="loc-chip" @click="goCommunityOf('chip')">🏢 {{ exploreLoc.commName }}</a>
              </div>
            </div>
            <div class="info-right">
              <div v-if="isUnlisted" class="price-label">下架前開價</div>
              <div class="big-price">{{ formatMoney(selectedProperty.totalPrice) }}</div>
              <div class="price-meta" v-if="selectedProperty.totalPrice < selectedProperty.lastPrice">
                已降 {{ Math.abs(selectedProperty.totalPrice - selectedProperty.lastPrice) }} 萬 ({{ ((1 - selectedProperty.totalPrice/selectedProperty.lastPrice)*100).toFixed(1) }}%)
              </div>
              <div class="price-meta" v-else-if="relistDrop">比前次刊登低 {{ relistDrop.amount }} 萬 ({{ relistDrop.pct }}%)</div>
              <div class="price-meta text-dim" v-else-if="isLocked(selectedProperty)">🔒 降價資訊需解鎖</div>
            </div>
          </div>
          <ul v-if="detailSummary.length" class="verdict-list" data-module="summary">
            <li v-for="(s, i) in detailSummary" :key="i">{{ s }}</li>
          </ul>
          <div class="spec-row">
            <span><label>格局</label>{{ selectedProperty.layout }}</span>
            <span><label>建坪</label>{{ selectedProperty.areaBuilding }} 坪</span>
            <span><label>單價</label>{{ formatUniPrice(selectedProperty) }}</span>
            <span><label>樓層</label>{{ selectedProperty.floor }} / {{ selectedProperty.totalfloor }} F</span>
            <span><label>屋齡</label>{{ String(selectedProperty.age || '-').replace(/年$/, '') }} 年</span>
          </div>
          <div class="hero-cta">
            <template v-if="!isUnlisted">
              <button @click="openPaywall(selectedProperty, 'notify')" class="btn-primary">🔔 降價時通知我</button>
              <a class="outbound-link" @click="openOutbound">前往信義房屋查看原始物件 ↗</a>
            </template>
            <button v-else-if="exploreLoc && exploreLoc.district" @click="goDistrictOf('cta')" class="btn-primary">看{{ exploreLoc.district }}在架物件</button>
            <span class="updated-note">資料更新 {{ (selectedProperty.updatedDate || '').slice(0, 10) }}</span>
          </div>
        </div>

        <div class="detail-grid" :class="{ single: !hasCompare }">
          <div class="card detail-chart-card" data-module="trend">
            <div class="card-title-row">
              <h3>價格軌跡</h3>
              <span v-if="priceCadence" class="cadence-note">調價 {{ priceCadence.count }} 次 · 平均 {{ priceCadence.avgGap }} 天一次 · 距上次 {{ priceCadence.since }} 天</span>
            </div>
            <div v-if="trendLock" class="chart-box paywall-chart">
              <svg class="paywall-placeholder" viewBox="0 0 300 120" preserveAspectRatio="none" aria-hidden="true">
                <polyline points="0,30 60,30 60,52 140,52 140,70 220,70 220,88 300,88" fill="none" stroke="currentColor" stroke-width="4"/>
              </svg>
              <div class="paywall-overlay">
                <div class="paywall-title">🔒 {{ trendLock.changeCount ? `此物件共改價 ${trendLock.changeCount} 次` : '完整調價時間軸' }}</div>
                <p v-if="trendLock.firstPrice && trendLock.firstPrice > trendLock.current.totalPrice" class="paywall-desc">
                  首次開價 {{ formatMoney(trendLock.firstPrice) }}，累計已降 <span class="text-red">{{ trendLock.firstPrice - trendLock.current.totalPrice }} 萬</span>
                </p>
                <p class="paywall-desc">解鎖看每一次在什麼時候、降了多少</p>
                <button class="btn-primary" @click="openPaywall(selectedProperty)">解鎖完整軌跡</button>
              </div>
            </div>
            <div v-else-if="trendRows.length === 1" class="chart-empty">
              <p>{{ isUnlisted ? '刊登期間' : '刊登至今' }}沒有調價紀錄，價格維持 {{ formatMoney(trendRows[0].price) }}。</p>
              <p v-if="explore && explore.similar.length" class="text-dim">往下看{{ exploreLoc && exploreLoc.district }}總價相近的物件 ↓</p>
            </div>
            <div v-else-if="trendRows.length" key="property-trend" class="chart-box"><canvas id="propertyTrendChart"></canvas></div>
            <div v-if="relistGapNote" class="trend-legend">
              <span class="legend-prev">前次刊登</span><span class="legend-cur">本次刊登</span><span class="text-dim">{{ relistGapNote }}</span>
            </div>
            <p v-if="relistInfo && relistInfo.relistCount && relistInfo.locked" class="text-dim paywall-note">
              🔁 此物件曾於 {{ relistInfo.previous[0].lastSeen.slice(0, 10) }} 下架，{{ relistInfo.previous[0].gapDays }} 天後以新編號重刊；前次刊登的價格已鎖定。<a class="paywall-link" @click="openPaywall(selectedProperty, 'relist')">解鎖前次刊登 →</a>
            </p>

            <template v-if="trendLock">
              <div v-if="trendLock.latest" class="table-wrapper trend-table">
                <table class="premium-table">
                  <thead><tr><th>調整日期</th><th>調整前</th><th>調整後</th><th>變動</th></tr></thead>
                  <tbody>
                    <tr v-if="trendLock.firstPrice && trendLock.firstPrice !== trendLock.latest.fromPrice" class="paywall-blur-row">
                      <td>🔒 {{ trendLock.changeCount - 1 }} 次較早的調價</td>
                      <td>首次開價 {{ formatMoney(trendLock.firstPrice) }}</td>
                      <td>{{ formatMoney(trendLock.latest.fromPrice) }}</td>
                      <td class="text-red">-{{ trendLock.firstPrice - trendLock.latest.fromPrice }} 萬</td>
                    </tr>
                    <tr>
                      <td>{{ trendLock.latest.changedDate.slice(0, 10) }}</td>
                      <td>{{ formatMoney(trendLock.latest.fromPrice) }}</td>
                      <td>{{ formatMoney(trendLock.latest.toPrice) }}</td>
                      <td :class="trendLock.latest.toPrice < trendLock.latest.fromPrice ? 'text-red' : 'text-purple'">
                        {{ trendLock.latest.toPrice < trendLock.latest.fromPrice ? '-' : '+' }}{{ Math.abs(trendLock.latest.toPrice - trendLock.latest.fromPrice) }} 萬
                        ({{ ((trendLock.latest.toPrice / trendLock.latest.fromPrice - 1) * 100).toFixed(1) }}%)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="text-dim paywall-note">此物件不在免費開放範圍內，調價紀錄需解鎖查看。</p>
            </template>
            <template v-else-if="trendRows.length > 1">
              <button class="detail-toggle" @click="showTrendTable = !showTrendTable">{{ showTrendTable ? '收起逐筆明細 ▲' : `看逐筆明細（${trendRows.length} 筆）▼` }}</button>
              <div v-if="showTrendTable" class="table-wrapper trend-table">
                <table class="premium-table">
                  <thead><tr><th>日期</th><th>價格</th><th>變動</th></tr></thead>
                  <tbody>
                    <template v-for="(r, idx) in trendRows">
                      <tr v-if="r.gapBefore" :key="'gap' + idx" class="gap-row"><td colspan="3">🔁 下架 {{ r.gapBefore }} 天後以新編號重刊</td></tr>
                      <tr :key="idx" :class="{ 'prev-row': r.prev }">
                        <td>{{ r.date }}<span v-if="r.prev" class="text-dim">（前次刊登）</span></td>
                        <td>{{ formatMoney(r.price) }}</td>
                        <td>
                          <span v-if="idx > 0 && r.price !== trendRows[idx-1].price" :class="r.price < trendRows[idx-1].price ? 'text-red' : 'text-purple'">
                            {{ r.price < trendRows[idx-1].price ? '-' : '+' }}{{ Math.round(Math.abs(r.price - trendRows[idx-1].price) * 10) / 10 }} 萬
                            ({{ ((r.price / trendRows[idx-1].price - 1) * 100).toFixed(1) }}%)
                          </span>
                          <span v-else class="text-dim">{{ idx === 0 ? '首次開價' : (r.end ? '下架' : '—') }}</span>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </template>
          </div>

          <div v-if="hasCompare" class="card compare-card" data-module="compare">
            <h3>這個價格合理嗎</h3>
            <template v-if="communityCompare && communityCompare.count">
              <div class="compare-title">同社區比價<span class="text-dim">（{{ communityCompare.parking ? '含' : '不含' }}車位，在架 {{ communityCompare.count }} 筆）</span></div>
              <div class="compare-bar">
                <div class="compare-dot" :style="{ left: compareDotPos + '%' }"></div>
              </div>
              <div class="compare-scale"><span>{{ communityCompare.min }}</span><span>中位 {{ communityCompare.median }}</span><span>{{ communityCompare.max }}</span></div>
              <p class="compare-desc">
                本物件每坪約 <strong>{{ communityCompare.pricePerPing }} 萬</strong>，{{ compareRankLabel }}，
                <span :class="communityCompare.pricePerPing > communityCompare.median ? 'text-red' : 'text-green'">{{ compareVsMedian }}</span>
              </p>
            </template>
            <div v-if="communityCompare && communityCompare.deals" class="compare-deals">
              <div class="compare-title">實價登錄成交<span class="text-dim">（近一年，{{ communityCompare.parking ? '含' : '不含' }}車位）</span></div>
              <p class="compare-desc">
                成交 {{ communityCompare.deals.count }} 筆，每坪中位數 <strong>{{ communityCompare.deals.median }} 萬</strong>；
                本物件開價
                <span :class="communityCompare.deals.askVsDeal > 0 ? 'text-red' : 'text-green'">{{ communityCompare.deals.askVsDeal > 0 ? '高於' : '低於' }}成交 {{ Math.abs(communityCompare.deals.askVsDeal) }}%</span>
              </p>
              <table v-if="communityCompare.deals.rows" class="deals-table">
                <tr v-for="(d, i) in communityCompare.deals.rows.slice(0, 6)" :key="i">
                  <td>{{ d.dealDate }}</td><td>{{ d.floorText }}</td><td>{{ d.areaPing }} 坪</td><td>{{ formatMoney(d.totalPrice) }}</td>
                </tr>
              </table>
              <p v-else class="compare-desc"><a class="paywall-link" style="margin-left:0" @click="openPaywall(selectedProperty, 'deals')">🔒 解鎖逐筆成交明細 →</a></p>
              <div class="compare-source">資料來源：{{ communityCompare.deals.source }}；以路段、總樓層與屋齡比對到社區，可能有誤差</div>
            </div>
            <div v-if="showRoadDeals" class="compare-deals" data-module="road_deals">
              <div class="compare-title">同路段實價登錄<span class="text-dim">（{{ explore.roadDeals.road }}，近一年，{{ selectedProperty.isParking ? '含' : '不含' }}車位）</span></div>
              <p class="compare-desc">
                成交 {{ explore.roadDeals.count }} 筆，每坪中位數 <strong>{{ explore.roadDeals.median }} 萬</strong><template v-if="explore.roadDeals.askVsDeal != null">；本物件開價
                <span :class="explore.roadDeals.askVsDeal > 0 ? 'text-red' : 'text-green'">{{ explore.roadDeals.askVsDeal > 0 ? '高於' : '低於' }}中位數 {{ Math.abs(explore.roadDeals.askVsDeal) }}%</span></template>
              </p>
              <table v-if="explore.roadDeals.rows" class="deals-table">
                <tr v-for="(d, i) in explore.roadDeals.rows.slice(0, 6)" :key="i">
                  <td>{{ d.dealDate }}</td><td>{{ d.floorText }}</td><td>{{ d.areaPing }} 坪</td><td>{{ formatMoney(d.totalPrice) }}</td>
                </tr>
              </table>
              <p v-else class="compare-desc"><a class="paywall-link" style="margin-left:0" @click="openPaywall(selectedProperty, 'deals')">🔒 解鎖逐筆成交明細 →</a></p>
              <div class="compare-source">資料來源：{{ explore.roadDeals.source }}；同路段屋齡、格局不同，僅供參考</div>
            </div>
          </div>
        </div>

        <div v-if="explore && (explore.similar.length || explore.districtDrops.length || explore.pulse)" class="card related-card" data-module="similar">
          <div class="related-header">
            <h3>{{ exploreLoc.district || '附近' }}還可以看</h3>
            <div class="seg-tabs">
              <button :class="{ active: similarTab === 'similar' }" @click="similarTab = 'similar'">相似物件</button>
              <button v-if="explore.districtDrops.length" :class="{ active: similarTab === 'drops' }" @click="similarTab = 'drops'">近 30 天降價</button>
            </div>
          </div>
          <div v-if="explore.pulse" class="pulse-row" data-module="pulse">
            <a @click="goDistrictOf('pulse_drops')">本週降價 <b>{{ explore.pulse.weekDrops }}</b></a>
            <a @click="goStaleOf">降價後久未售出 <b>{{ explore.pulse.stale }}</b></a>
            <a @click="goRelistOf">近 30 天換編號重刊 <b>{{ explore.pulse.relist30 }}</b></a>
          </div>
          <div v-if="similarItems.length" class="related-grid">
            <div v-for="(r, i) in similarItems" :key="r.houseNo" class="related-item" @click="openRelated(r.houseNo, similarTab === 'drops' ? 'district_drops' : 'similar', i)">
              <div class="related-name">{{ r.commName || r.name }}</div>
              <div class="related-meta">{{ r.district || extractDistrict(r.address) }}{{ r.road ? ' · ' + r.road : '' }}</div>
              <div class="related-price">
                <span class="price">{{ formatMoney(r.totalPrice) }}</span>
                <span v-if="r.lastPrice > r.totalPrice" class="drop-pill">-{{ Math.round((r.lastPrice - r.totalPrice) * 10) / 10 }} 萬</span>
                <span v-else-if="similarTab === 'drops' && isLocked(r)" class="lock-pill">🔒 降幅</span>
              </div>
              <div class="related-meta">{{ r.layout }} · {{ r.areaBuilding }} 坪<template v-if="r.areaBuilding"> · 每坪 {{ (r.totalPrice / r.areaBuilding).toFixed(1) }} 萬</template></div>
            </div>
          </div>
        </div>
      </section>

      <section v-else-if="currentView === 'notfound'" class="detail-container">
        <div class="card notfound-card">
          <h1>查無此物件</h1>
          <p class="text-dim">編號 {{ notFoundId }} 不在追蹤紀錄中，可能是輸入錯誤，或從未出現在信義房屋的刊登清單。</p>
          <div class="notfound-actions">
            <button class="btn-primary" @click="goToDashboard">看市場動能</button>
            <button class="btn-outline" @click="goToList">全市物件清單</button>
          </div>
        </div>
      </section>
    </main>

    <!-- 付費解鎖（假門：功能尚未開放，先收集候補名單） -->
    <div v-if="paywallModal.open" class="paywall-modal-backdrop" @click.self="closePaywall">
      <div class="paywall-modal card" role="dialog" aria-modal="true">
        <button class="paywall-close" @click="closePaywall" aria-label="關閉">×</button>
        <template v-if="!paywallModal.done">
          <template v-if="paywallModal.feature === 'notify'">
            <h3>降價通知即將推出</h3>
            <p class="paywall-desc">物件降價時寄信通知你，功能準備中。留下 Email，開放時第一時間通知你。</p>
          </template>
          <template v-else>
            <h3>付費解鎖即將推出</h3>
            <p class="paywall-desc">我們正在準備完整價格軌跡、首次開價、累計降幅與降價排行等進階資料。留下 Email，開放時第一時間通知你。</p>
          </template>
          <input type="email" v-model.trim="paywallModal.email" placeholder="you@example.com" class="search-box paywall-input" @keyup.enter="submitWaitlist">
          <label class="paywall-consent">
            <input type="checkbox" v-model="paywallModal.consent">
            <span>我同意本站蒐集上述 Email，僅用於通知付費功能上線，不會提供給第三方；可隨時來信要求刪除。</span>
          </label>
          <p v-if="paywallModal.error" class="text-red paywall-note">{{ paywallModal.error }}</p>
          <button class="btn-primary full-width-btn" :disabled="paywallModal.submitting" @click="submitWaitlist">通知我</button>
        </template>
        <template v-else>
          <h3>已加入候補名單</h3>
          <p class="paywall-desc">功能開放時會寄信通知你，謝謝！</p>
          <button class="btn-outline full-width-btn" @click="closePaywall">關閉</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Chart from 'chart.js';

const API_BASE = '';

export default {
  data() {
    const host = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);
    const cityFromUrl = urlParams.get('city');
    let defaultCity = 'taichung';
    if (host.includes('taichung')) defaultCity = 'taichung';
    
    return {
      currentCity: cityFromUrl || defaultCity, 
      cities: [],
      currentView: 'home', currentDistrict: 'All', marketTrend: [],
      lastSyncTime: '-', searchQuery: '', filters: { sortBy: 'dropCountDesc' },
      isMobile: false, charts: { main: null, bar: null, rise: null, pie: null, scatter: null, property: null, ageDist: null, dropPctDist: null, dropAmtDist: null },
      historyMap: {}, selectedProperty: null, propertyHistory: [], trendLock: null, showcaseItems: [], relistItems: [], relistInfo: null,
      dropBehaviorData: [], dropBehaviorStats: null, listingAge: null, staleData: null, dropExit: null, communityCompare: null, communityHeatData: [], matrixSort: 'dropRate',
      paywallModal: { open: false, email: '', consent: false, error: '', submitting: false, done: false, feature: null },
      // 物件頁延伸內容（docs/ENGAGEMENT_PRD.md）
      explore: null, similarTab: 'similar', detailFromInside: false, notFoundId: '', showTrendTable: false,
      // 新增分頁與統計狀態
      statsData: [], highlights: { eyeCatching: [], topDrops: [], topIncreases: [], recentChanges: [] },
      pagination: { items: [], total: 0, page: 1, pageSize: 50, loading: false, hasMore: true }
    };
  },
  computed: {
    currentCityName() { return (this.cities.find(c => c.id === this.currentCity) || {}).name || ''; },
    currentCityShort() { return (this.cities.find(c => c.id === this.currentCity) || {}).short || ''; },
    viewTitle() { 
      const distLabel = this.currentDistrict === 'All' ? this.currentCityShort : this.currentDistrict;
      if (this.currentView === 'insights') return `${distLabel} 市場洞察分析`; 
      if (this.currentView === 'list') return this.currentDistrict === 'All' ? `${this.currentCityShort}全市物件清單` : `${this.currentDistrict} 物件清單`;
      if (this.currentView === 'detail') return '物件行情深度分析';
      return this.currentDistrict === 'All' ? `${this.currentCityShort}房市大數據` : `${this.currentDistrict} 分析報告`; 
    },
    viewSubtitle() { 
      const distLabel = this.currentDistrict === 'All' ? '全市' : this.currentDistrict;
      if (this.currentView === 'insights') return `針對 ${distLabel} 的掛牌壽命、降價行為與社區熱度深度分析。`; 
      if (this.currentView === 'list') return `目前顯示 ${distLabel} 的完整物件清單。`;
      if (this.currentView === 'detail') return '包含歷史價格變動、單價分析與市場定位。';
      return '即時監控市場價格波動趨勢、區域單價變動與物件活躍動能。'; 
    },
    uniqueDistricts() { return this.statsData.map(i => i.district).sort(); },
    filteredSummary() { 
      // 儀表板使用的摘要數據，改用分頁列表的第一頁或是 highlights
      return this.pagination.items; 
    },
    searchedList() { 
      let list = this.pagination.items;
      // 強制二次過濾，確保後端緩存或延遲不影響結果
      if (this.currentDistrict !== 'All') {
        list = list.filter(i => (i.address || '').includes(this.currentDistrict));
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(i => 
          (i.commName || '').toLowerCase().includes(q) || 
          (i.name || '').toLowerCase().includes(q) || 
          (i.address || '').toLowerCase().includes(q)
        );
      }
      return list; 
    },
    topDropItems() { return this.highlights.topDrops; },
    eyeCatchingList() { return this.highlights.eyeCatching; },
    newCount() {
      const active = this.currentDistrict === 'All' ? this.statsData : this.statsData.filter(d => d.district === this.currentDistrict);
      return active.reduce((a, b) => a + (b.newCount || 0), 0);
    },
    dropCount() {
      const active = this.currentDistrict === 'All' ? this.statsData : this.statsData.filter(d => d.district === this.currentDistrict);
      return active.reduce((a, b) => a + (b.dropCount || 0), 0);
    },
    dropPercent() { 
      const total = this.currentDistrict === 'All' ? this.pagination.total : this.statsData.find(d => d.district === this.currentDistrict)?.count || 0;
      if (!total) return 0;
      return ((this.dropCount / total) * 100).toFixed(1);
    },
    avgUniPrice() { 
      const active = this.currentDistrict === 'All' ? this.statsData : this.statsData.filter(d => d.district === this.currentDistrict);
      if (!active.length) return '0.0';
      const sum = active.reduce((a, b) => a + (b.avgUniPrice * b.count), 0);
      const count = active.reduce((a, b) => a + b.count, 0);
      return count ? (sum / count).toFixed(1) : '0.0';
    },
    avgTotalPrice() { 
      const active = this.currentDistrict === 'All' ? this.statsData : this.statsData.filter(d => d.district === this.currentDistrict);
      if (!active.length) return 0;
      const sum = active.reduce((a, b) => a + (b.avgPrice * b.count), 0); // 這裡簡化處理
      const count = active.reduce((a, b) => a + b.count, 0);
      return count ? Math.round(sum / count) : 0;
    },
    priceChangeRate() { return 0; }, // 此欄位目前由後端 stats 補齊
    marketStability() {
      if (this.marketTrend.length < 3) return '數據累積中';
      const lastThree = this.marketTrend.slice(-3);
      const trend = lastThree[2].dropCount - lastThree[0].dropCount;
      if (trend > 5) return '下修壓力大';
      if (trend < -5) return '盤整回溫中';
      return '高檔震盪';
    },
    riseItems() { return this.highlights.topIncreases || []; },
    avgListingDays() { return this.listingAge && this.listingAge.avgDays != null ? this.listingAge.avgDays : '-'; },
    longListedCount() { return this.listingAge ? this.listingAge.over30 : 0; },
    longListedPct() { return this.listingAge ? this.listingAge.over30Pct : 0; },
    veryLongListedCount() { return this.listingAge ? this.listingAge.over90 : 0; },
    veryLongListedPct() { return this.listingAge ? this.listingAge.over90Pct : 0; },
    topDropBehavior() { return this.dropBehaviorData.slice(0, 10); },
    // 彙總由後端在遮蔽前計算（付費牆開啟時逐筆明細不完整）
    avgDropCount() { return this.dropBehaviorStats ? this.dropBehaviorStats.avgDropCount.toFixed(1) : 0; },
    avgTotalDropAmt() { return this.dropBehaviorStats ? this.dropBehaviorStats.avgTotalDropAmt : 0; },
    avgDropPct() { return this.dropBehaviorStats ? this.dropBehaviorStats.avgDropPct.toFixed(1) : 0; },
    districtMatrix() { return this.statsData; },
    compareDotPos() {
      const c = this.communityCompare;
      if (!c || c.max === c.min) return 50;
      return Math.round((c.pricePerPing - c.min) / (c.max - c.min) * 100);
    },
    compareRankLabel() {
      const c = this.communityCompare;
      if (c.rank === 1) return '同社區最便宜';
      if (c.rank === c.count) return '同社區最貴';
      return `第 ${c.rank} 便宜`;
    },
    compareVsMedian() {
      const c = this.communityCompare;
      const diff = Math.round((c.pricePerPing / c.median - 1) * 1000) / 10;
      if (Math.abs(diff) < 1) return '與社區中位數相當';
      return diff > 0 ? `比中位數高 ${diff}%` : `比中位數低 ${-diff}%`;
    },
    exploreLoc() {
      const ex = this.explore, p = this.selectedProperty;
      return ex && p && ex.houseNo === p.houseNo ? ex.location : null;
    },
    isUnlisted() { return !!(this.selectedProperty && this.selectedProperty.is_unlisted); },
    listingDaysText() {
      const st = this.exploreLoc && this.explore.status;
      if (!st || st.listingDays == null) return '';
      return `${st.censored ? '至少' : '約'} ${st.listingDays} 天`;
    },
    statusLine() {
      if (!this.exploreLoc) return '';
      const st = this.explore.status, days = this.listingDaysText;
      if (st.unlisted) return `已下架（約 ${(st.unlistedDate || '').slice(0, 10)}）${days ? '· 刊登' + days : ''}`;
      return days ? `在架${days}` : '';
    },
    showRoadDeals() {
      return !!(this.exploreLoc && this.explore.roadDeals && !(this.communityCompare && this.communityCompare.deals));
    },
    similarItems() {
      if (!this.explore) return [];
      return this.similarTab === 'drops' ? this.explore.districtDrops : this.explore.similar;
    },
    hasCompare() {
      return !!((this.communityCompare && (this.communityCompare.count || this.communityCompare.deals)) || this.showRoadDeals);
    },
    relistPrev() {
      // 前次刊登（由舊到新）；鎖定時沒有價格，回空陣列
      const r = this.relistInfo;
      return r && r.relistCount && !r.locked ? r.previous.slice().reverse() : [];
    },
    trendRows() {
      // 價格軌跡：前次刊登接上本次刊登（docs/ENGAGEMENT_PRD.md E10）
      if (this.trendLock) return [];
      const rows = [], prev = this.relistPrev;
      prev.forEach((p, i) => {
        const gap = i > 0 ? prev[i - 1].gapDays : null;
        const pts = p.trend.map(t => ({ date: t.changedDate.slice(0, 10), price: t.totalPrice }));
        if (!pts.length) pts.push({ date: p.firstSeen.slice(0, 10), price: p.lastPrice });
        pts.push({ date: p.lastSeen.slice(0, 10), price: p.lastPrice, end: true });
        pts.forEach((x, k) => rows.push(Object.assign(x, { prev: true, gapBefore: k === 0 ? gap : null })));
      });
      this.propertyHistory.forEach((h, k) => rows.push({
        date: h.updatedDate.slice(0, 10), price: h.totalPrice, prev: false,
        gapBefore: k === 0 && prev.length ? prev[prev.length - 1].gapDays : null
      }));
      return rows;
    },
    relistDrop() {
      const p = this.relistPrev[this.relistPrev.length - 1], now = this.selectedProperty && this.selectedProperty.totalPrice;
      if (!p || !now || !(p.lastPrice > now)) return null;
      return { amount: Math.round((p.lastPrice - now) * 10) / 10, pct: ((1 - now / p.lastPrice) * 100).toFixed(1) };
    },
    relistGapNote() {
      const p = this.relistPrev[this.relistPrev.length - 1];
      return p && this.trendRows.length > 1 ? `虛線為下架空窗（${p.gapDays} 天）` : '';
    },
    detailSummary() {
      // 固定模板的行情判讀（docs/ENGAGEMENT_PRD.md E3）
      if (!this.exploreLoc) return [];
      const ex = this.explore, p = this.selectedProperty, st = ex.status, out = [];
      const days = this.listingDaysText, n = st.changeCount;
      const rl = this.relistInfo && this.relistInfo.relistCount ? this.relistInfo : null;
      const last = this.relistPrev[this.relistPrev.length - 1];
      const curFirst = this.propertyHistory.length ? this.propertyHistory[0].totalPrice : p.totalPrice;
      if (last) {
        const d = Math.round((curFirst - last.lastPrice) * 10) / 10;
        const diff = d < 0 ? `實際降 ${-d} 萬（${((d / last.lastPrice) * 100).toFixed(1)}%）` : (d > 0 ? `反而高 ${d} 萬` : '價格相同');
        out.push(`換編號重刊：前次刊登下架前開價 ${this.formatMoney(last.lastPrice)}，${last.gapDays} 天後以新編號開價 ${this.formatMoney(curFirst)}，${diff}。`);
      } else if (rl) {
        out.push(`曾於 ${rl.previous[0].lastSeen.slice(0, 10)} 下架，${rl.previous[0].gapDays} 天後以新編號重刊；前次刊登的價格需解鎖。`);
      }
      const scope = rl ? '本次刊登' : '刊登';
      let first = null;
      if (this.trendRows.length > 1) first = this.trendRows[0].price;
      else if (this.trendLock && this.trendLock.firstPrice) first = this.trendLock.firstPrice;
      const dropPct = first && first > p.totalPrice ? ((1 - p.totalPrice / first) * 100).toFixed(1) : null;
      const drop = dropPct ? `，${last ? '從前次刊登首次開價' : ''}累計降 ${dropPct}%` : '';
      if (st.unlisted) {
        out.push(`${scope}${days || ''}後下架，期間${n ? `調價 ${n} 次${drop}` : (n === 0 ? '未調價' : '的調價紀錄需解鎖')}。`);
      } else if (n > 0) {
        out.push(`${days ? scope + days + '，' : ''}調價 ${n} 次${drop}。`);
      } else if (n === 0) {
        let s = `${days ? scope + days + '，' : ''}尚未調價`;
        if (ex.peer && ex.peer.changedPct != null) s += `；${ex.location.district}總價相近的在架物件有 ${ex.peer.changedPct}% 調過價`;
        out.push(s + '。');
      }
      const pp = p.areaBuilding ? (p.totalPrice / p.areaBuilding).toFixed(1) : null;
      const vs = (diff, label) => Math.abs(diff) < 1 ? `與${label}近一年成交中位數相當` : `${diff > 0 ? '高於' : '低於'}${label}近一年成交中位數 ${Math.abs(diff)}%`;
      const cd = this.communityCompare && this.communityCompare.deals;
      if (pp && cd) out.push(`每坪開價約 ${pp} 萬，${vs(cd.askVsDeal, '同社區')}。`);
      else if (pp && ex.roadDeals && ex.roadDeals.askVsDeal != null) out.push(`每坪開價約 ${pp} 萬，${vs(ex.roadDeals.askVsDeal, ex.roadDeals.road)}。`);
      return out;
    },
    priceCadence() {
      // 只陳述過去的節奏；回測顯示無法預測下次降價（docs/DATA_PRODUCTS_PRD.md D2）
      const h = this.propertyHistory;
      if (h.length < 3) return null;
      const changes = h.slice(0, -1).map(x => new Date(x.updatedDate));
      const day = 24 * 60 * 60 * 1000;
      return {
        count: changes.length,
        avgGap: Math.round((changes[changes.length - 1] - changes[0]) / day / (changes.length - 1)),
        since: Math.floor((new Date() - changes[changes.length - 1]) / day)
      };
    }
  },
  watch: { 
    filteredSummary() { this.safeUpdateCharts(); }, 
    currentView() { this.safeUpdateCharts(); }, 
    searchQuery() { if (this.currentView === 'home') return; if (this._searchTimer) clearTimeout(this._searchTimer); this._searchTimer = setTimeout(() => { this.onDistrictChange(); }, 400); },
    'filters.sortBy'() { if (this.currentView === 'home') return; this.resetPagination(); this.fetchSummary(); },
    highlights() { this.safeUpdateCharts(); },
    relistInfo() { if (this.currentView === 'detail') this.$nextTick(() => this.renderPropertyTrendChart()); },
    dropBehaviorData() { if (this.currentView === 'insights') this.$nextTick(() => { this.renderAgeDistChart(); this.renderDropDistCharts(); }); } 
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    window.addEventListener('popstate', () => this.handleRoute());
    this.init();
  },
  methods: {
    async init() {
      const r = await axios.get(`${API_BASE}/api/cities`);
      const backendCities = r.data;
      const allCities = [
        { id: 'taichung', name: '台中市', short: '台中' },
        { id: 'taipei', name: '台北市', short: '台北' },
        { id: 'newtaipei', name: '新北市', short: '新北', disabled: true },
        { id: 'taoyuan', name: '桃園市', short: '桃園', disabled: true },
        { id: 'tainan', name: '台南市', short: '台南', disabled: true },
        { id: 'kaohsiung', name: '高雄市', short: '高雄', disabled: true }
      ];
      this.cities = allCities.map(c => {
        const active = backendCities.find(bc => bc.id === c.id);
        return active ? { ...c, disabled: false } : c;
      });
      await this.fetchSummary();
      await this.fetchMarketTrend();
      this.fetchShowcase();
      this.fetchRelist();
      this.handleRoute();
      this.safeUpdateCharts();
      // 設定初始標題
      const city = this.cities.find(c => c.id === this.currentCity);
      if (city) {
        const label = city.name.replace('市', '') + '房產動態';
        document.title = `${label} | 即時房價追蹤與降價監控`;
      }
    },
    async switchCity(id) {
      if (id) {
        const city = this.cities.find(c => c.id === id);
        if (!city || city.disabled) return;
        
        this.currentCity = id;
      }
      
      this.currentDistrict = 'All';
      this.resetPagination();
      this.historyMap = {};
      this.dropBehaviorData = [];
      this.communityHeatData = [];
      this.currentView = 'dashboard';
      
      const city = this.cities.find(c => c.id === this.currentCity);
      if (city) {
        const label = city.name.replace('市', '') + '房產動態';
        document.title = `${label} | 即時房價追蹤與降價監控`;
      }

      await this.fetchSummary();
      await this.fetchMarketTrend();
      this.fetchShowcase();
      this.fetchRelist();
      this.safeUpdateCharts();
      this.pushUrl('/dashboard');
    },
    handleRoute() {
      const path = window.location.pathname;
      const propertyMatch = path.match(/^\/property\/(.+)$/);
      const districtMatch = path.match(/^\/district\/(.+)$/);
      const communityMatch = path.match(/^\/community\/(.+)$/);

      if (propertyMatch) {
        const houseNo = decodeURIComponent(propertyMatch[1]);
        this.showDetailByHouseNo(houseNo, true);
      } else if (districtMatch) {
        const district = decodeURIComponent(districtMatch[1]);
        this.currentDistrict = district;
        this.currentView = 'dashboard';
      } else if (communityMatch) {
        const commName = decodeURIComponent(communityMatch[1]);
        this.searchQuery = commName;
        this.currentView = 'list';
        this.onDistrictChange();
      } else if (path === '/dashboard') {
        this.currentView = 'dashboard';
      } else if (path === '/listings') {
        this.currentView = 'list';
      } else if (path === '/insights') {
        this.currentView = 'insights';
        if (!this.dropBehaviorData.length) {
          this.fetchInsights().then(() => {
            this.$nextTick(() => { this.renderAgeDistChart(); this.renderDropDistCharts(); });
          });
        }
      } else {
        this.currentView = 'home';
      }
    },
    goToHome() {
      this.currentView = 'home';
      this.pushUrl('/');
    },
    goToList() {
      this.currentView = 'list';
      this.pushUrl('/listings');
    },
    goToListByCommunity(commName) {
      this.currentDistrict = 'All';
      this.searchQuery = commName;
      this.currentView = 'list';
      this.pushUrl('/listings');
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this.resetPagination();
      this.fetchSummary();
      window.scrollTo(0, 0);
    },
    parseAndGo() {
      const input = this.searchQuery.trim();
      if (!input) return;

      // 純 ID：3-10 碼英數字（信義 ID 如 79462W）
      if (/^[A-Z0-9]{3,10}$/i.test(input)) {
        this.searchQuery = '';
        this.showDetailByHouseNo(input.toUpperCase());
        return;
      }

      // URL：從連結中提取物件編號（支援 sinyi.com.tw/buy/house/XXXXX）
      const match = input.match(/\/house\/([A-Z0-9]+)/i);
      if (match) {
        this.searchQuery = '';
        this.showDetailByHouseNo(match[1].toUpperCase());
      } else {
        // 關鍵字搜尋：切換到清單頁
        this.goToList();
        this.onDistrictChange();
      }
    },
    pushUrl(path) {
      if (window.location.pathname !== path) {
        history.pushState(null, '', path);
      }
    },
    async goToInsights() {
      this.currentView = 'insights';
      this.pushUrl('/insights');
      if (!this.dropBehaviorData.length) await this.fetchInsights();
      this.$nextTick(() => { this.renderAgeDistChart(); this.renderDropDistCharts(); });
    },
    async fetchInsights() {
      try {
        const params = { city: this.currentCity, district: this.currentDistrict };
        const [b, c, a, st, ex] = await Promise.all([
          axios.get(`${API_BASE}/api/insights/drop-behavior`, { params }),
          axios.get(`${API_BASE}/api/insights/community-heat`, { params }),
          axios.get(`${API_BASE}/api/insights/listing-age`, { params }),
          axios.get(`${API_BASE}/api/insights/stale`, { params }),
          axios.get(`${API_BASE}/api/insights/drop-exit`, { params })
        ]);
        this.dropExit = ex.data;
        this.listingAge = a.data;
        this.staleData = st.data;
        this.$nextTick(() => this.renderAgeDistChart());
        this.dropBehaviorStats = b.data.stats;
        this.dropBehaviorData = b.data.items;
        this.communityHeatData = c.data;
      } catch (e) { console.error(e); }
    },
    async showDetailByHouseNo(houseNo, skipPush) { 
      // 優先從現有列表找，避免額外 API 請求
      const allItems = [
        ...this.pagination.items,
        ...this.highlights.eyeCatching,
        ...this.highlights.topDrops,
        ...this.highlights.topIncreases,
        ...this.highlights.recentChanges
      ];
      const item = allItems.find(i => i.houseNo === houseNo);
      if (item) {
        this.showDetail(item, skipPush);
      } else {
        // 若找不到（例如從外部連結進入或在洞察頁面），則直接從 API 抓取完整資料
        try {
          const res = await axios.get(`${API_BASE}/api/houses/${houseNo}`);
          if (res.data && res.data.length > 0) {
            this.showDetail(res.data[0], skipPush);
          } else {
            this.notFoundId = houseNo;
            this.currentView = 'notfound';
            if (!skipPush) this.pushUrl(`/property/${houseNo}`);
            this.track('property_view', { listing_status: 'notfound' });
          }
        } catch (e) { console.error('無法取得物件詳情', e); }
      }
    },
    renderAgeDistChart() {
      const ctx = document.getElementById('ageDistChart'); if (!ctx) return;
      if (this.charts.ageDist) this.charts.ageDist.destroy();
      const buckets = {};
      ((this.listingAge && this.listingAge.buckets) || []).forEach(b => { buckets[b.label] = b.count; });
      this.charts.ageDist = new Chart(ctx, {
        type: 'bar',
        data: { labels: Object.keys(buckets), datasets: [{ label: '物件數', data: Object.values(buckets), backgroundColor: ['rgba(56,189,248,0.4)','rgba(251,191,36,0.4)','rgba(251,146,60,0.4)','rgba(251,113,133,0.4)','rgba(167,139,250,0.4)'], borderColor: ['#38bdf8','#fbbf24','#fb923c','#fb7185','#a78bfa'], borderWidth: 2 }] },
        options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { xAxes: [{ ticks: { fontColor: '#94a3b8' }, gridLines: { display: false } }], yAxes: [{ ticks: { fontColor: '#94a3b8', beginAtZero: true }, gridLines: { color: 'rgba(255,255,255,0.05)' } }] } }
      });
    },
    renderDropDistCharts() {
      if (!this.dropBehaviorStats) return;
      this._buildDistChart(
        'dropPctDistChart', 'dropPctDist',
        this.dropBehaviorStats.dropPcts,
        2, v => `${v}%`, '降價幅度 (%)', 'rgba(251,113,133,0.45)', '#fb7185'
      );
      this._buildDistChart(
        'dropAmtDistChart', 'dropAmtDist',
        this.dropBehaviorStats.totalDrops,
        50, v => `${v}萬`, '降價金額 (萬)', 'rgba(56,189,248,0.45)', '#38bdf8'
      );
    },
    _buildDistChart(canvasId, chartKey, values, binSize, labelFn, xLabel, barColor, lineColor) {
      const ctx = document.getElementById(canvasId); if (!ctx) return;
      if (this.charts[chartKey]) this.charts[chartKey].destroy();
      if (!values.length) return;
      const maxVal = Math.max(...values);
      const numBins = Math.ceil(maxVal / binSize) + 1;
      const counts = new Array(numBins).fill(0);
      values.forEach(v => { const idx = Math.min(Math.floor(v / binSize), numBins - 1); counts[idx]++; });
      const labels = Array.from({ length: numBins }, (_, i) => labelFn(i * binSize));
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const std = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length) || 1;
      const scale = values.length * binSize;
      const bell = Array.from({ length: numBins }, (_, i) => {
        const x = i * binSize + binSize / 2;
        return (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mean) / std) ** 2) * scale;
      });
      this.charts[chartKey] = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            { type: 'bar', label: '物件數', data: counts, backgroundColor: barColor, borderColor: lineColor, borderWidth: 1.5 },
            { type: 'line', label: '常態分佈', data: bell, fill: false, borderColor: 'rgba(167,139,250,0.9)', borderWidth: 2.5, pointRadius: 0, lineTension: 0.4 }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          legend: { display: true, labels: { fontColor: '#94a3b8', fontSize: 11, boxWidth: 12 } },
          scales: {
            xAxes: [{ ticks: { fontColor: '#94a3b8', fontSize: 10, maxRotation: 45 }, gridLines: { display: false }, scaleLabel: { display: true, labelString: xLabel, fontColor: '#64748b' } }],
            yAxes: [{ ticks: { fontColor: '#94a3b8', beginAtZero: true, callback: v => Number.isInteger(v) ? v : '' }, gridLines: { color: 'rgba(255,255,255,0.05)' }, scaleLabel: { display: true, labelString: '物件數', fontColor: '#64748b' } }]
          }
        }
      });
    },
    checkMobile() { this.isMobile = window.innerWidth < 768; },
    onDistrictChange() { 
      this.resetPagination(); 
      this.fetchSummary(); 
      this.fetchInsights();
      this.safeUpdateCharts(); 
    },
    resetPagination() { this.pagination.page = 1; this.pagination.items = []; this.pagination.hasMore = true; },
    async fetchSummary() {
      if (this.pagination.loading) return;
      this.pagination.loading = true;
      try {
        const params = { city: this.currentCity, district: this.currentDistrict };
        const [stats, high, list] = await Promise.all([
          axios.get(`${API_BASE}/api/summary/stats`, { params }),
          axios.get(`${API_BASE}/api/summary/highlights`, { params }),
          axios.get(`${API_BASE}/api/summary/list`, { params: { 
            ...params,
            search: this.searchQuery,
            sortBy: this.filters.sortBy,
            page: this.pagination.page
          } })
        ]);
        
        this.statsData = stats.data;
        this.highlights = high.data;
        this.pagination.items = list.data.items;
        this.pagination.total = list.data.total;
        this.pagination.hasMore = list.data.items.length === 50;
        this.lastSyncTime = new Date().toLocaleTimeString('zh-TW', { hour12: false, hour: '2-digit', minute: '2-digit' });
      } catch (e) { console.error(e); } finally { this.pagination.loading = false; }
    },
    async loadMore() {
      if (this.pagination.loading || !this.pagination.hasMore) return;
      this.pagination.page++;
      this.pagination.loading = true;
      try {
        const res = await axios.get(`${API_BASE}/api/summary/list`, { params: { 
          city: this.currentCity, 
          district: this.currentDistrict,
          search: this.searchQuery,
          sortBy: this.filters.sortBy,
          page: this.pagination.page
        } });
        this.pagination.items = [...this.pagination.items, ...res.data.items];
        this.pagination.hasMore = res.data.items.length === 50;
      } catch (e) { console.error(e); } finally { this.pagination.loading = false; }
    },
    async fetchRelist() {
      try {
        const res = await axios.get(`${API_BASE}/api/relist/recent`, { params: { city: this.currentCity, limit: 5 } });
        this.relistItems = res.data;
      } catch (e) { this.relistItems = []; }
    },
    formatUniPrice(p) {
      // 信義遇含車位物件只給文字說明，改以 總價 / 建坪 估算
      const m = String(p.uniPrice || '').match(/(\d+\.?\d*)/);
      if (m) return `${m[1]} 萬/坪`;
      if (p.totalPrice && p.areaBuilding) return `約 ${(p.totalPrice / p.areaBuilding).toFixed(1)} 萬/坪（含車位）`;
      return '-';
    },
    async fetchShowcase() {
      try {
        const res = await axios.get(`${API_BASE}/api/paywall/showcase`, { params: { city: this.currentCity } });
        this.showcaseItems = res.data;
      } catch (e) { this.showcaseItems = []; }
    },
    async fetchMarketTrend() { try { const res = await axios.get(`${API_BASE}/api/marketTrend`, { params: { city: this.currentCity } }); this.marketTrend = res.data; } catch (e) { console.error(e); } },
    extractDistrict(addr) { 
      if (!addr) return '其他';
      const m = addr.match(/(.*?市)(.*?區)/); 
      return m ? m[2] : '其他'; 
    },
    selectDistrict(d) { 
      this.currentDistrict = d; 
      // 若不在清單或儀表板，則導向儀表板；若在清單則維持在清單並篩選
      if (this.currentView !== 'list' && this.currentView !== 'dashboard') {
        this.currentView = 'dashboard';
      }
      this.onDistrictChange();
      window.scrollTo(0, 0);
      const basePath = this.currentView === 'list' ? '/listings' : '/dashboard';
      this.pushUrl(d === 'All' ? basePath : `/district/${d}`);
    },
    goToDashboard() {
      this.currentView = 'dashboard';
      this.currentDistrict = 'All';
      this.onDistrictChange();
      this.pushUrl('/dashboard');
    },
    openLink(url) { if (url) window.open(url, '_blank'); },
    formatMoney(v) { return v >= 10000 ? (v/10000).toFixed(1) + ' 億' : v + ' 萬'; },
    getDayLabel(dateStr) { if (!dateStr) return '-'; const diff = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24)); return diff <= 0 ? '今日' : `${diff}天`; },
    getDayColor(dateStr) { const d = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24)); return d <= 3 ? 'hot' : 'normal'; },
    safeUpdateCharts() { if (this._updateTimer) clearTimeout(this._updateTimer); this._updateTimer = setTimeout(() => this.updateCharts(), 250); },
    async updateCharts() {
      if (this.currentView === 'detail' && this.selectedProperty) {
        this.renderPropertyTrendChart();
        return;
      }
      if (this.currentView === 'insights') { this.renderAgeDistChart(); this.renderDropDistCharts(); return; }
      if (this.currentView !== 'dashboard') return;
      await this.fetchTopHistories();
      this.renderMainPulseChart(); this.renderBar(); this.renderRiseBar(); this.renderPriceRangeChart(); this.renderPie();
    },
    async showDetail(item, skipPush) {
      this.selectedProperty = item;
      this.currentView = 'detail';
      this.trendLock = null;
      this.relistInfo = null;
      this.communityCompare = null;
      this.explore = null;
      this.similarTab = 'similar';
      this.showTrendTable = false;
      // 從站內點進來才有「上一頁」可回；外部進站改導向行政區
      this.detailFromInside = !skipPush;
      window.scrollTo(0, 0);
      axios.get(`${API_BASE}/api/community-compare/${item.houseNo}`)
        .then(res => { if (this.selectedProperty === item) { this.communityCompare = res.data; this.$nextTick(this.observeModules); } })
        .catch(() => {});
      axios.get(`${API_BASE}/api/property/${item.houseNo}/explore`)
        .then(res => {
          if (this.selectedProperty !== item) return;
          this.explore = res.data;
          const st = res.data.status;
          this.track('property_view', {
            listing_status: st.unlisted ? (res.data.successor ? 'relisted' : 'unlisted') : 'active',
            has_price_change: st.changeCount == null ? 'locked' : String(st.changeCount > 0),
            has_comm: String(!!res.data.location.commName),
            has_road_deals: String(!!res.data.roadDeals),
            paywall_level: res.data.paywallLevel || item.visibility || 'FULL'
          });
          this.$nextTick(this.observeModules);
        })
        .catch(() => {});
      this.propertyHistory = [];
      if (!skipPush) this.pushUrl(`/property/${item.houseNo}`);
      axios.get(`${API_BASE}/api/relist/${item.houseNo}`)
        .then(res => { if (this.selectedProperty === item) this.relistInfo = res.data; })
        .catch(() => {});
      try {
        const res = await axios.get(`${API_BASE}/api/priceTrend/${item.houseNo}`);
        if (res.data && res.data.locked) {
          this.trendLock = res.data;
          return;
        }
        this.propertyHistory = res.data.sort((a, b) => new Date(a.updatedDate) - new Date(b.updatedDate));
        this.$nextTick(() => this.renderPropertyTrendChart());
      } catch (e) { console.error(e); }
    },
    isLocked(item) { return !!item && item.visibility === 'LOCKED'; },
    track(name, params) {
      // GA4 事件（docs/ENGAGEMENT_PRD.md E0）；不送任何可識別個人的資訊
      try { if (window.gtag) window.gtag('event', name, params || {}); } catch (e) { /* ignore */ }
    },
    observeModules() {
      if (this._moduleObserver) this._moduleObserver.disconnect();
      if (!('IntersectionObserver' in window) || !this.selectedProperty) return;
      const houseNo = this.selectedProperty.houseNo;
      if (!this._moduleSeen || this._moduleSeen.houseNo !== houseNo) this._moduleSeen = { houseNo, seen: new Set() };
      const seen = this._moduleSeen.seen;
      this._moduleObserver = new IntersectionObserver(entries => {
        entries.forEach(e => {
          const m = e.target.dataset.module;
          if (m && e.isIntersecting && !seen.has(m)) { seen.add(m); this.track('module_view', { module: m }); }
        });
      }, { threshold: 0.4 });
      this.$el.querySelectorAll('.detail-container [data-module]').forEach(el => this._moduleObserver.observe(el));
    },
    cityNameOf(id) { return (this.cities.find(c => c.id === id) || {}).name || ''; },
    async ensureCity(city) {
      if (city && city !== this.currentCity && this.cities.some(c => c.id === city && !c.disabled)) await this.switchCity(city);
    },
    openRelated(houseNo, module, position) {
      this.track('related_click', { module, position, target_type: 'property' });
      this.showDetailByHouseNo(houseNo);
    },
    async goCityOf(module) {
      const loc = this.exploreLoc; if (!loc) return;
      this.track('related_click', { module, target_type: 'city' });
      await this.ensureCity(loc.city);
      this.goToDashboard();
    },
    async goDistrictOf(module) {
      const loc = this.exploreLoc; if (!loc || !loc.district) return;
      this.track('related_click', { module, target_type: 'district' });
      await this.ensureCity(loc.city);
      this.selectDistrict(loc.district);
    },
    async goCommunityOf(module) {
      const loc = this.exploreLoc; if (!loc || !loc.commName) return;
      this.track('related_click', { module, target_type: 'community' });
      await this.ensureCity(loc.city);
      this.goToListByCommunity(loc.commName);
    },
    async goRoadOf(module) {
      const loc = this.exploreLoc; if (!loc || !loc.road) return;
      this.track('related_click', { module, target_type: 'road' });
      await this.ensureCity(loc.city);
      this.currentDistrict = loc.district || 'All';
      this.searchQuery = loc.road;
      this.currentView = 'list';
      this.pushUrl('/listings');
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this.resetPagination();
      this.fetchSummary();
      window.scrollTo(0, 0);
    },
    async goStaleOf() {
      const loc = this.exploreLoc; if (!loc) return;
      this.track('related_click', { module: 'pulse_stale', target_type: 'insights' });
      await this.ensureCity(loc.city);
      this.currentDistrict = loc.district || 'All';
      this.dropBehaviorData = [];
      await this.goToInsights();
      this.scrollToId('stale-section');
    },
    async goRelistOf() {
      const loc = this.exploreLoc; if (!loc) return;
      this.track('related_click', { module: 'pulse_relist', target_type: 'dashboard' });
      await this.ensureCity(loc.city);
      this.goToDashboard();
      this.scrollToId('relist-section');
    },
    scrollToId(id) {
      setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 600);
    },
    goBack() { history.back(); },
    openOutbound() {
      this.track('outbound_click', { target: 'sinyi' });
      this.openLink(this.selectedProperty && this.selectedProperty.shareURL);
    },
    openPaywall(item, feature) {
      // feature：從哪個付費功能點進來（例如 stale、relist），供假門驗證比較各功能的點擊率
      this.paywallModal = { open: true, email: '', consent: false, error: '', submitting: false, done: false, feature: feature || null };
      this.track('paywall_cta', { feature: feature || 'trend' });
      axios.post(`${API_BASE}/api/paywall/event`, {
        event: 'cta_click', entityType: feature ? 'feature' : 'property', entityId: feature || (item && item.houseNo), path: window.location.pathname
      }).catch(() => {});
    },
    closePaywall() { this.paywallModal.open = false; },
    async submitWaitlist() {
      const m = this.paywallModal;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(m.email)) { m.error = '請輸入正確的 Email'; return; }
      if (!m.consent) { m.error = '請勾選同意後再送出'; return; }
      m.error = ''; m.submitting = true;
      try {
        await axios.post(`${API_BASE}/api/paywall/waitlist`, { email: m.email, consent: true, sourceUrl: window.location.pathname });
        m.done = true;
      } catch (e) {
        m.error = (e.response && e.response.data && e.response.data.error) || '送出失敗，請稍後再試';
      } finally { m.submitting = false; }
    },
    renderPropertyTrendChart() {
      const ctx = document.getElementById('propertyTrendChart'), rows = this.trendRows;
      if (!ctx || rows.length < 2) return;
      if (this.charts.property) this.charts.property.destroy();
      // 前次刊登用灰色虛線，並連到本次第一個點，讓下架空窗看得出來
      const firstCur = rows.findIndex(r => !r.prev);
      const prevData = rows.map((r, i) => (r.prev || i === firstCur ? r.price : null));
      const curData = rows.map(r => (r.prev ? null : r.price));
      const hasPrev = rows.some(r => r.prev);
      const datasets = [{ label: '本次刊登 (萬)', data: curData, borderColor: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.1)', borderWidth: 4, pointRadius: 6, pointBackgroundColor: '#38bdf8', fill: !hasPrev, tension: 0.1 }];
      if (hasPrev) datasets.push({ label: '前次刊登 (萬)', data: prevData, borderColor: '#94a3b8', borderDash: [6, 6], borderWidth: 3, pointRadius: 5, pointBackgroundColor: '#94a3b8', fill: false, tension: 0 });
      this.charts.property = new Chart(ctx, {
        type: 'line',
        data: { labels: rows.map(r => r.date), datasets },
        options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { xAxes: [{ ticks: { fontColor: '#94a3b8' } }], yAxes: [{ ticks: { fontColor: '#94a3b8' } }] } }
      });
    },
    async fetchTopHistories() {
      const topIds = [...this.topDropItems.map(i => i.houseNo), ...this.riseItems.map(i => i.houseNo)];
      const missing = topIds.filter(id => !this.historyMap[id]);
      if (!missing.length) return;
      const res = await axios.get(`${API_BASE}/api/priceTrendBatch`, { params: { ids: missing.join(',') } });
      Object.entries(res.data).forEach(([id, history]) => {
        this.historyMap[id] = history.sort((a, b) => new Date(a.updatedDate) - new Date(b.updatedDate));
      });
    },
    renderMainPulseChart() {
      const ctx = document.getElementById('marketTrendChart'); if (!ctx) return;
      if (this.charts.main) this.charts.main.destroy();
      const trend = this.marketTrend.length > 1 ? this.marketTrend.slice(1) : this.marketTrend;
      this.charts.main = new Chart(ctx, {
        type: 'line',
        data: {
          labels: trend.map(d => d.day.substring(5)),
          datasets: [
            { label: '降價件數', data: trend.map(d => d.dropCount), borderColor: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.1)', borderWidth: 3, pointRadius: 3, fill: true, tension: 0.4 },
            { label: '平均降幅', data: trend.map(d => d.avgDrop), borderColor: '#fb7185', borderDash: [5, 5], borderWidth: 2, pointRadius: 0, fill: false, tension: 0.4, yAxisID: 'y2' }
          ]
        },
        options: { responsive: true, maintainAspectRatio: false, legend: { display: false }, scales: { xAxes: [{ ticks: { fontColor: '#94a3b8' } }], yAxes: [{ id: 'y1', ticks: { fontColor: '#94a3b8' } }, { id: 'y2', position: 'right', ticks: { display: false } }] } }
      });
    },
    renderBar() {
      const ctx = document.getElementById('barChart'); if (!ctx) return;
      if (this.charts.bar) this.charts.bar.destroy();
      const items = this.topDropItems;
      this.charts.bar = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: items.map(i => (i.commName||i.name).substring(0,8)),
          datasets: [
            { label: '現價', data: items.map(i => i.totalPrice), backgroundColor: '#34d399' },
            { 
              label: '最近一次降幅', 
              data: items.map(i => {
                const h = this.historyMap[i.houseNo] || [];
                if (h.length < 2) return i.dropAmt || 0;
                return h[h.length-2].totalPrice - i.totalPrice;
              }), 
              backgroundColor: '#fb7185' 
            },
            { 
              label: '歷次累計降幅', 
              data: items.map(i => {
                const h = this.historyMap[i.houseNo] || [];
                if (h.length < 2) return (i.priceFirst && i.lastPrice) ? Math.max(0, i.priceFirst - i.lastPrice) : 0;
                // 正確邏輯：初始開價 - 倒數第二次開價 = 歷次累積降幅
                return Math.max(0, h[0].totalPrice - h[h.length-2].totalPrice);
              }), 
              backgroundColor: '#fbbf24' 
            }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false, legend: { display: false },
          onClick: (evt) => {
            const active = this.charts.bar.getElementAtEvent(evt);
            if (active.length > 0) this.showDetail(items[active[0]._index]);
          },
          tooltips: {
            mode: 'index', intersect: false,
            filter: (item, data) => {
              const val = data.datasets[item.datasetIndex].data[item.index];
              return val > 0 || item.datasetIndex === 0;
            },
            callbacks: {
              title: (ts) => items[ts[0].index].commName || items[ts[0].index].name,
              label: (t) => {
                const labels = ['目前現價', '最近一次降幅', '歷次累計降幅'];
                return `${labels[t.datasetIndex]}: ${t.xLabel} 萬`;
              },
              footer: (ts) => {
                const item = items[ts[0].index];
                const history = this.historyMap[item.houseNo] || [];
                const first = history.length ? history[0].totalPrice : (item.priceFirst || item.lastPrice);
                if (!first) return '原始開價: 🔒 解鎖查看';
                return `原始開價: ${first} 萬`;
              }
            }
          },
          scales: {
            xAxes: [{ stacked: true, ticks: { fontColor: '#94a3b8', beginAtZero: true } }],
            yAxes: [{ stacked: true, ticks: { fontColor: '#94a3b8' }, afterFit: (s) => { s.width = 110; } }]
          }
        }
      });
    },
    renderRiseBar() {
      const ctx = document.getElementById('riseBarChart'); if (!ctx) return;
      if (this.charts.rise) this.charts.rise.destroy();
      const items = this.riseItems;
      this.charts.rise = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: items.map(i => (i.commName||i.name).substring(0,8)),
          datasets: [
            { 
              label: '初始開價', 
              data: items.map(i => {
                const h = this.historyMap[i.houseNo] || [];
                return h.length > 0 ? h[0].totalPrice : (i.priceFirst || (i.lastPrice != null ? i.lastPrice : i.totalPrice));
              }), 
              backgroundColor: '#a78bfa' 
            },
            { 
              label: '累計漲幅', 
              data: items.map(i => {
                const h = this.historyMap[i.houseNo] || [];
                const first = h.length ? h[0].totalPrice : (i.priceFirst || i.lastPrice);
                if (!first) return 0;
                return Math.max(0, i.totalPrice - first);
              }), 
              backgroundColor: '#818cf8' 
            }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false, legend: { display: false },
          onClick: (evt) => {
            const active = this.charts.rise.getElementAtEvent(evt);
            if (active.length > 0) this.showDetail(items[active[0]._index]);
          },
          tooltips: {
            mode: 'index', intersect: false,
            filter: (item, data) => {
              const val = data.datasets[item.datasetIndex].data[item.index];
              return val > 0 || item.datasetIndex === 0;
            },
            callbacks: {
              title: (ts) => items[ts[0].index].commName || items[ts[0].index].name,
              label: (t) => {
                const labels = ['初始開價', '累計漲幅'];
                return `${labels[t.datasetIndex]}: ${t.xLabel} 萬`;
              },
              footer: (ts) => {
                const item = items[ts[0].index];
                return `目前現價: ${item.totalPrice} 萬`;
              }
            }
          },
          scales: {
            xAxes: [{ stacked: true, ticks: { fontColor: '#94a3b8', beginAtZero: true } }],
            yAxes: [{ stacked: true, ticks: { fontColor: '#94a3b8' }, afterFit: (s) => { s.width = 110; } }]
          }
        }
      });
    },
    renderPriceRangeChart() {
      const ctx = document.getElementById('priceRangeChart'); if (!ctx) return;
      if (this.charts.scatter) this.charts.scatter.destroy();
      const bks = [{ label: '1千萬↓', min: 0, max: 1000 }, { label: '1-1.5千萬', min: 1000, max: 1500 }, { label: '1.5-2千萬', min: 1500, max: 2000 }, { label: '2-3千萬', min: 2000, max: 3000 }, { label: '3千萬↑', min: 3000, max: 999999 }];
      const sts = bks.map(b => {
        // LOCKED 物件沒有 lastPrice，排除以免低估降價比例
        const inB = this.filteredSummary.filter(i => i.totalPrice >= b.min && i.totalPrice < b.max && !this.isLocked(i));
        const total = inB.length;
        const drops = inB.filter(i => i.totalPrice < i.lastPrice).length;
        return { label: b.label, total, pct: total > 0 ? ((drops / total) * 100).toFixed(1) : 0 };
      });
      this.charts.scatter = new Chart(ctx, {
        type: 'bar',
        data: { labels: sts.map(s => s.label), datasets: [ { label: '市場權重 (物件數)', data: sts.map(s => s.total), backgroundColor: 'rgba(56, 189, 248, 0.3)', yAxisID: 'y1' }, { label: '修正強度 (降價%)', data: sts.map(s => s.pct), type: 'line', borderColor: '#fb7185', borderWidth: 3, pointBackgroundColor: '#fb7185', fill: false, yAxisID: 'y2' } ] },
        options: {
          responsive: true, maintainAspectRatio: false, legend: { position: 'top', labels: { fontColor: '#94a3b8', fontSize: 10 } },
          scales: { xAxes: [{ ticks: { fontColor: '#94a3b8' }, gridLines: { display: false } }], yAxes: [ { id: 'y1', position: 'left', ticks: { fontColor: '#94a3b8', beginAtZero: true }, gridLines: { color: 'rgba(255,255,255,0.05)' } }, { id: 'y2', position: 'right', ticks: { fontColor: '#fb7185', beginAtZero: true, callback: v => v + '%' }, gridLines: { display: false } } ] },
          tooltips: { mode: 'index', intersect: false, callbacks: { label: (t, d) => { const l = d.datasets[t.datasetIndex].label; const v = d.datasets[t.datasetIndex].data[t.index]; return l.includes('%') ? `${l}: ${v}%` : `${l}: ${v} 件`; } } }
        }
      });
    },
    renderPie() {
      const ctx = document.getElementById('pieChart'); if (!ctx) return;
      if (this.charts.pie) this.charts.pie.destroy();
      let counts = {};
      if (this.currentDistrict === 'All') { 
        this.statsData.forEach(d => { if (d.district !== '其他') counts[d.district] = d.count; }); 
      }
      else { counts = { '1千萬以下': 0, '1-2千萬': 0, '2千萬以上': 0 }; this.filteredSummary.forEach(i => { if (i.totalPrice<1000) counts['1千萬以下']++; else if (i.totalPrice<2000) counts['1-2千萬']++; else counts['2千萬以上']++; }); }
      const labels = Object.keys(counts);
      this.charts.pie = new Chart(ctx, {
        type: 'doughnut',
        data: { labels, datasets: [{ data: Object.values(counts), backgroundColor: ['#38bdf8', '#818cf8', '#fb7185', '#34d399', '#fbbf24', '#a78bfa'], borderWidth: 0 }] },
        options: {
          responsive: true, maintainAspectRatio: false, cutoutPercentage: 65,
          onClick: (evt) => { const active = this.charts.pie.getElementAtEvent(evt); if (active.length > 0 && this.currentDistrict === 'All') { this.selectDistrict(labels[active[0]._index]); } },
          legend: { position: 'bottom', labels: { fontColor: '#94a3b8', fontSize: 10, padding: 15 } }
        }
      });
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
:root {
  --bg: #0f172a; --card: #1e293b; --sidebar: #0b0f1a;
  --primary: #38bdf8; --text: #f1f5f9; --text-dim: #94a3b8;
  --red: #fb7185; --green: #34d399; --orange: #fbbf24; --purple: #a78bfa; --teal: #2dd4bf;
  --border: rgba(255, 255, 255, 0.08);
}
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Inter', sans-serif; background: var(--bg); color: var(--text); overflow-x: hidden; }
.main-content { margin-left: 280px; padding: 3rem; width: calc(100% - 280px); min-height: 100vh; }
.sidebar { width: 280px; background: var(--sidebar); position: fixed; height: 100vh; z-index: 100; padding: 2.5rem 1.5rem; border-right: 1px solid var(--border); display: flex; flex-direction: column; }
.brand { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
.brand-clickable { cursor: pointer; transition: opacity 0.2s; }
.brand-clickable:hover { opacity: 0.8; }
.city-switcher { margin-bottom: 2rem; }
.city-select { width: 100%; background: rgba(255,255,255,0.06); border: 1px solid var(--border); color: #fff; padding: 0.6rem 1rem; border-radius: 0.6rem; font-size: 0.95rem; font-weight: 700; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.8rem center; }
.city-select:focus { outline: none; border-color: var(--primary); }
.city-select option { background: #1e293b; color: #fff; }
.logo-box { width: 32px; height: 32px; background: var(--primary); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 900; border-radius: 6px; }
.brand-name { font-weight: 800; font-size: 1.25rem; letter-spacing: 1px; color: #fff; }
.nav-btn { background: none; border: none; color: var(--text-dim); padding: 1rem 1.25rem; border-radius: 0.8rem; cursor: pointer; text-align: left; font-weight: 600; transition: 0.2s; margin-bottom: 0.5rem; width: 100%; }
.nav-btn.active { background: rgba(56, 189, 248, 0.1); color: var(--primary); }
.nav-btn:hover:not(.active) { background: rgba(255,255,255,0.03); color: #fff; }
.sidebar-footer { margin-top: auto; padding-top: 1.5rem; border-top: 1px solid var(--border); }
.last-sync { font-size: 0.7rem; color: var(--text-dim); margin-top: 0.6rem; }
.top-bar { margin-bottom: 3rem; display: flex; justify-content: space-between; align-items: flex-start; }
.breadcrumb { font-size: 0.8rem; color: var(--text-dim); margin-bottom: 0.75rem; display: flex; align-items: center; gap: 0.5rem; }
.crumb-link { cursor: pointer; transition: 0.2s; }
.crumb-link:hover { color: var(--primary); }
.top-bar h1 { 
  font-size: 2.75rem; 
  font-weight: 800; 
  color: #fff; 
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #fff 30%, var(--text-dim) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
}
.title-desc {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.desc-tag {
  background: rgba(251, 113, 133, 0.1);
  color: var(--red);
  font-size: 0.65rem;
  font-weight: 900;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  border: 1px solid rgba(251, 113, 133, 0.2);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.subtitle { 
  font-size: 0.95rem; 
  color: var(--text-dim); 
  font-weight: 500;
  opacity: 0.8;
  letter-spacing: 0.01em;
}
.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; margin-bottom: 3rem; }
.stat-card { background: var(--card); padding: 1.75rem; border-radius: 1.5rem; border: 1px solid var(--border); height: 180px; display: flex; flex-direction: column; justify-content: space-between; position: relative; transition: 0.3s; }
.stat-card:hover { transform: translateY(-4px); border-color: rgba(255,255,255,0.15); z-index: 9999; }
.stat-label { font-size: 0.8rem; font-weight: 700; color: var(--text-dim); display: flex; align-items: center; gap: 0.5rem; }
.main-num { font-size: 2.4rem; font-weight: 800; color: #fff; line-height: 1; }
.main-num .unit { font-size: 0.85rem; font-weight: 400; color: var(--text-dim); margin-left: 0.25rem; }
.sub-info { display: flex; align-items: baseline; gap: 0.5rem; font-size: 0.9rem; }
.info-val { font-weight: 800; font-size: 1.1rem; }
.blue-theme .info-val { color: var(--primary); } .blue-theme .bar-fill { background: var(--primary); }
.red-theme .info-val { color: var(--red); } .red-theme .bar-fill { background: var(--red); }
.purple-theme .info-val { color: var(--purple); } .purple-theme .bar-fill { background: var(--purple); }
.teal-theme .info-val { color: var(--teal); } .teal-theme .bar-fill { background: var(--teal); }
.visual-bar { height: 6px; background: rgba(255,255,255,0.05); border-radius: 10px; overflow: hidden; }
.analysis-grid.secondary { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
.analysis-grid.single { margin-bottom: 2rem; }
.chart-legend .dot.purple { background: #818cf8; }
.chart-legend .dot.blue { background: #a78bfa; }
.visual-row { display: grid; grid-template-columns: 3fr 1fr; gap: 1.5rem; margin-bottom: 3rem; height: 420px; }
.chart-box { flex: 1; position: relative; width: 100%; min-height: 0; }
.chart-box.small { height: 300px; }
.chart-row.two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
.chart-box canvas { width: 100% !important; height: 100% !important; }
.eye-catching-section { margin-bottom: 4rem; }
.section-header { margin-bottom: 1.5rem; display: flex; align-items: baseline; gap: 1.25rem; }
.eye-catching-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1.5rem; }
.hot-card { background: linear-gradient(145deg, #1e293b, #0f172a); border: 1px solid rgba(56, 189, 248, 0.15); border-radius: 1.5rem; padding: 1.75rem; cursor: pointer; transition: 0.3s; position: relative; }
.hot-card:hover { transform: translateY(-6px); border-color: var(--primary); }
.hot-tag { position: absolute; top: 0; right: 0; background: var(--red); color: #fff; font-size: 0.65rem; font-weight: 900; padding: 0.3rem 0.8rem; border-bottom-left-radius: 0.8rem; }
.hot-name { font-weight: 800; color: #fff; font-size: 1.05rem; margin-bottom: 0.6rem; line-height: 1.5; }
.hot-district { font-size: 0.85rem; color: var(--text-dim); margin-bottom: 1.5rem; }
.hot-price { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.8rem; }
.hot-price .price { color: var(--primary); font-weight: 800; font-size: 1.25rem; }
.drop-pill { background: rgba(251, 113, 133, 0.15); color: var(--red); font-size: 0.8rem; font-weight: 800; padding: 0.2rem 0.6rem; border-radius: 2rem; }
.hot-save { color: var(--orange); font-weight: 800; font-size: 0.9rem; margin-bottom: 1.25rem; }
.hot-footer { font-size: 0.75rem; color: var(--text-dim); border-top: 1px solid var(--border); padding-top: 1rem; }
.card { background: var(--card); border-radius: 1.5rem; border: 1px solid var(--border); padding: 2rem; display: flex; flex-direction: column; overflow: hidden; }
.card-header h3 { font-size: 1.15rem; font-weight: 800; color: #fff; margin-bottom: 1.25rem; }
.premium-table { width: 100%; border-collapse: collapse; }
.premium-table th { text-align: left; padding: 1.25rem 1rem; color: var(--text-dim); font-size: 0.75rem; font-weight: 800; border-bottom: 2px solid var(--border); }
.premium-table td { padding: 1.5rem 1rem; border-bottom: 1px solid var(--border); }
.price-flow { display: flex; align-items: center; gap: 0.8rem; }
.current-price { color: var(--primary); font-weight: 800; font-size: 1.15rem; }
.old-price { color: var(--text-dim); opacity: 0.5; font-size: 0.85rem; }
.trend-tag { color: var(--red); background: rgba(251, 113, 133, 0.1); padding: 0.2rem 0.6rem; border-radius: 0.5rem; font-weight: 800; font-size: 0.8rem; }
.rise-tag { color: var(--green); background: rgba(52, 211, 153, 0.1); }
.btn-primary { background: var(--primary); color: #000; border: none; padding: 0.8rem 1.5rem; border-radius: 0.8rem; font-weight: 800; cursor: pointer; transition: 0.2s; }
.btn-outline { background: none; border: 1px solid var(--border); color: #fff; padding: 0.6rem 1.2rem; border-radius: 0.8rem; cursor: pointer; font-size: 0.8rem; font-weight: 700; }
.btn-outline:hover { border-color: var(--primary); color: var(--primary); }
.btn-sm { padding: 0.5rem 1rem; font-size: 0.8rem; }
.btn-xs { padding: 0.4rem 0.8rem; font-size: 0.75rem; border-radius: 0.5rem; }
.clickable-row { cursor: pointer; transition: background 0.18s; }
.clickable-row:hover { background: rgba(56, 189, 248, 0.05); }
.name-link { color: #fff; font-weight: 700; transition: color 0.18s; display: flex; align-items: center; gap: 0.4rem; }
.clickable-row:hover .name-link { color: var(--primary); }
.name-arrow { font-size: 0.9rem; opacity: 0; transform: translateX(-4px); transition: opacity 0.18s, transform 0.18s; color: var(--primary); }
.clickable-row:hover .name-arrow { opacity: 1; transform: translateX(0); }

/* Form controls */
.custom-select { background: var(--card); color: var(--text); border: 1px solid var(--border); padding: 0.6rem 2rem 0.6rem 1rem; border-radius: 0.6rem; font-size: 0.85rem; cursor: pointer; outline: none; appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.7rem center; transition: 0.2s; }
.custom-select:hover, .custom-select:focus { border-color: var(--primary); }
.custom-select option { background: var(--card); color: var(--text); }
.search-box { background: var(--card); color: var(--text); border: 1px solid var(--border); padding: 0.65rem 1rem; border-radius: 0.6rem; font-size: 0.85rem; outline: none; width: 100%; transition: 0.2s; }
.search-box:focus { border-color: var(--primary); }
.search-box::placeholder { color: var(--text-dim); }
.btn-clear-filter { background: rgba(251, 113, 133, 0.1); color: var(--red); border: 1px solid rgba(251, 113, 133, 0.25); padding: 0.6rem 1rem; border-radius: 0.6rem; cursor: pointer; font-size: 0.8rem; font-weight: 700; transition: 0.2s; white-space: nowrap; }
.btn-clear-filter:hover { background: rgba(251, 113, 133, 0.2); }

/* Header layout */
.header-actions { display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap; }
.district-selector { display: flex; align-items: center; gap: 0.5rem; }
.selector-label { font-size: 0.85rem; color: var(--text-dim); white-space: nowrap; }
.title-section { display: flex; flex-direction: column; }
.main-title-row { 
  display: flex; 
  align-items: baseline; 
  gap: 1.25rem; 
  flex-wrap: wrap; 
}
.crumb-sep { opacity: 0.4; }
.crumb-item { color: var(--text); }

/* Stat cards extras */
.stat-value { display: flex; flex-direction: column; gap: 0.4rem; }
.info-label { font-size: 0.75rem; color: var(--text-dim); }
.dashboard-grid { display: flex; flex-direction: column; }

/* Section headers */
.section-header h3 { font-size: 1.25rem; font-weight: 800; color: #fff; }
.algo-tip { font-size: 0.75rem; color: var(--text-dim); }
.chart-legend { display: flex; gap: 1rem; font-size: 0.75rem; color: var(--text-dim); align-items: center; }
.chart-legend .dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 0.3rem; }
.chart-legend .dot.green { background: var(--green); }
.chart-legend .dot.red { background: var(--red); }
.card-header.compact { padding-bottom: 0; }
.card.no-padding { padding: 0; }
.card.no-padding .card-header { padding: 1.5rem 1.5rem 0.5rem; }
.card.no-padding .chart-box { padding: 0 1rem 1rem; }

/* Filter bar */
.filter-bar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; margin-bottom: 1.5rem; padding: 1.5rem 2rem; }
.filter-group { display: flex; flex-direction: column; gap: 0.45rem; }
.filter-group label { font-size: 0.7rem; color: var(--text-dim); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.filter-group .custom-select { width: 100%; }

/* List */
.list-container { display: flex; flex-direction: column; gap: 1.5rem; }

/* Badges */
.badge-btn { background: rgba(56, 189, 248, 0.1); color: var(--primary); border: 1px solid rgba(56, 189, 248, 0.2); padding: 0.3rem 0.7rem; border-radius: 0.5rem; cursor: pointer; font-size: 0.75rem; font-weight: 700; display: inline-block; transition: 0.2s; white-space: nowrap; }
.badge-btn:hover { background: rgba(56, 189, 248, 0.2); }
.day-badge { padding: 0.3rem 0.7rem; border-radius: 0.5rem; font-size: 0.75rem; font-weight: 700; display: inline-block; white-space: nowrap; }
.day-badge.hot { background: rgba(251, 113, 133, 0.15); color: var(--red); }
.day-badge.normal { background: rgba(148, 163, 184, 0.1); color: var(--text-dim); }

/* Property table cells */
.property-cell .name { color: #fff; font-weight: 700; margin-bottom: 0.25rem; }
.property-cell .meta { color: var(--text-dim); font-size: 0.8rem; }
.table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }

/* Detail page */
.back-row { margin-bottom: 1rem; }
.info-left { flex: 1; }
.info-left h1 { color: #fff; font-size: 1.75rem; font-weight: 800; }
.info-right { text-align: right; }
.text-dim { color: var(--text-dim); }

/* 詳情頁樣式 */
.detail-container { animation: fadeIn 0.4s ease-out; }
.detail-header { margin-bottom: 2rem; }
.detail-main-info { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 1.5rem; }
.detail-addr { color: var(--text-dim); margin-top: 0.5rem; }
.big-price { font-size: 3rem; font-weight: 800; color: var(--primary); line-height: 1; }
.price-meta { color: var(--red); font-weight: 700; margin-top: 0.5rem; text-align: right; }

.detail-grid { display: grid; grid-template-columns: 3fr 2fr; gap: 1.5rem; margin-bottom: 2rem; align-items: start; }
.detail-grid.single { grid-template-columns: 1fr; }
.detail-chart-card .chart-box { height: 280px; }

.full-width-btn { width: 100%; margin-top: 1rem; }
.text-red { color: var(--red); font-weight: 700; }
.text-purple { color: var(--purple); font-weight: 700; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.insights-container { animation: fadeIn 0.3s ease; }
.matrix-table th.sortable { cursor: pointer; user-select: none; }
.matrix-table th.sortable:hover { color: var(--primary); }
.matrix-table th.active { color: var(--primary); }
.text-orange { color: var(--orange); font-weight: 700; }
.text-green { color: var(--green); font-weight: 700; }
.info-tip { cursor: help; position: relative; border-bottom: 1px dotted var(--text-dim); }
.info-tip:hover::after { content: attr(data-tip); position: absolute; bottom: 120%; left: 50%; transform: translateX(-50%); background: #000; color: #fff; padding: 0.75rem 1rem; border-radius: 0.6rem; font-size: 0.75rem; white-space: nowrap; z-index: 9999; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
@media (max-width: 767px) {
  .info-tip:hover::after { left: 0; transform: none; white-space: normal; width: max-content; max-width: 70vw; }
}
.mobile-nav { display: none; }
@media (max-width: 767px) {
  .sidebar { display: none !important; }
  .main-content { margin-left: 0 !important; width: 100% !important; padding: 1.25rem !important; padding-bottom: 6rem !important; }

  /* Top bar */
  .top-bar { flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; align-items: stretch; }
  .top-bar h1 { 
    font-size: 1.75rem; 
    line-height: 1.2;
    background: none;
    -webkit-text-fill-color: #fff;
    letter-spacing: -0.01em;
  }
  .main-title-row {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  .title-desc {
    gap: 0.5rem;
  }
  .subtitle {
    font-size: 0.85rem;
    line-height: 1.4;
  }
  .desc-tag {
    padding: 0.1rem 0.4rem;
    font-size: 0.6rem;
  }
  .header-actions { width: 100%; }
  .district-selector { flex: 1; }
  .custom-select { width: 100%; flex: 1; }
  .selector-label { display: none; }

  /* Cards */
  .card { padding: 1.25rem; border-radius: 1rem; }
  .stats-row { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; margin-bottom: 1.5rem; }
  .stat-card { height: auto; padding: 1.25rem; border-radius: 1rem; }
  .main-num { font-size: 1.6rem; }
  .info-val { font-size: 0.9rem; }

  /* Charts */
  .visual-row { grid-template-columns: 1fr !important; height: auto; gap: 1rem; margin-bottom: 1.5rem; }
  .visual-row .card { height: 320px !important; margin-bottom: 0; }
  .analysis-grid.secondary { grid-template-columns: 1fr; gap: 1rem; }
  .chart-box.small { height: 260px; }
  .chart-row.two-col { grid-template-columns: 1fr; }
  .eye-catching-section { margin-bottom: 2rem; }
  .eye-catching-grid { grid-template-columns: 1fr; gap: 1rem; }
  .section-header { flex-direction: column; align-items: flex-start; gap: 0.4rem; margin-bottom: 1rem; }

  /* Filter bar */
  .filter-bar { grid-template-columns: 1fr; gap: 1rem; padding: 1.25rem; }

  /* Tables */
  .premium-table { font-size: 0.85rem; min-width: 100%; }
  .premium-table th { padding: 0.75rem 0.5rem; font-size: 0.7rem; }
  .premium-table td { padding: 1rem 0.5rem; }
  .current-price { font-size: 1rem; }
  .price-flow { flex-direction: column; align-items: flex-start; gap: 0.3rem; }

  /* Detail page */
  .detail-grid { grid-template-columns: 1fr !important; gap: 1rem; }
  .detail-main-info { flex-direction: column; align-items: flex-start; gap: 1rem; }
  .info-right { text-align: left; width: 100%; }
  .info-left h1 { font-size: 1.4rem; }
  .big-price { font-size: 2.25rem; }
  .price-meta { text-align: left; }
  .detail-header { padding: 1.25rem; }
  .hero-cta .btn-primary { width: 100%; }
  .hero-cta .outbound-link { flex: 1; text-align: left; }
  .spec-row { gap: 0.6rem 1.25rem; }
  .verdict-list { padding: 0.9rem 1rem 0.9rem 1.9rem; font-size: 0.9rem; }
}

.mobile-top-header {
  display: flex;
  justify-content: flex-start; /* 靠左對齊 */
  align-items: center;
  padding: 8px 16px;
  background: rgba(10, 18, 42, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  position: sticky;
  top: 0;
  z-index: 2000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
.mob-logo {
  font-weight: 900;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 1.25rem;
  font-family: 'Inter', sans-serif;
}
.mob-city-switcher {
  display: flex;
  background: rgba(0, 0, 0, 0.3);
  padding: 3px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.mob-city-btn {
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}
.mob-city-btn:active {
  transform: scale(0.92);
}
.mob-city-btn.active {
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.mob-city-btn.disabled {
  opacity: 0.3;
  cursor: not-allowed;
  filter: grayscale(1);
}
@media (max-width: 768px) {
  .main-container { padding-top: 0; padding-bottom: 80px; }
}

/* Mobile bottom nav */
@media (max-width: 767px) {
  .mobile-nav {
    display: flex;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    background: rgba(11, 15, 26, 0.97);
    backdrop-filter: blur(12px);
    border-top: 1px solid rgba(255,255,255,0.1);
    height: 5rem;
    justify-content: space-around;
    align-items: center;
    z-index: 1000;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .m-nav-btn {
    background: none;
    border: none;
    color: #4a5568;
    font-size: 0.7rem;
    font-weight: 600;
    font-family: inherit;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.3rem;
    padding: 0.5rem 1.2rem;
    border-radius: 12px;
    cursor: pointer;
    transition: color 0.2s, background 0.2s;
    position: relative;
  }
  .m-nav-btn svg { transition: stroke 0.2s; }
  .m-nav-btn.active {
    color: var(--primary);
    background: rgba(56, 189, 248, 0.08);
  }
  .m-nav-btn.active::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 20%;
    right: 20%;
    height: 2px;
    background: var(--primary);
    border-radius: 0 0 2px 2px;
  }
  .m-nav-btn:not(.active):active {
    color: #94a3b8;
    background: rgba(255,255,255,0.04);
  }
}

  /* Landing Page (Google Style) */
  .landing-page {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -3rem;
    min-height: 100vh;
    width: calc(100% + 6rem);
    padding: 2rem;
  }

  .landing-content {
    width: 100%;
    max-width: 680px;
    text-align: center;
    margin-top: -6vh;
  }

  .landing-hero {
    margin-bottom: 2.75rem;
    animation: fadeInUp 0.7s ease-out;
  }

  .landing-title {
    font-size: 4rem;
    font-weight: 800;
    margin: 0;
    background: linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -2px;
  }

  .landing-tagline {
    font-size: 0.9rem;
    color: #64748b;
    letter-spacing: 5px;
    text-transform: uppercase;
    margin-top: 0.6rem;
    font-weight: 500;
  }

  .g-search-wrap {
    animation: fadeInUp 0.7s ease-out 0.12s backwards;
    margin-bottom: 1.75rem;
  }

  .g-search-bar {
    display: flex;
    align-items: center;
    background: rgba(30, 41, 59, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 30px;
    padding: 0.5rem 0.5rem 0.5rem 1.25rem;
    gap: 0.75rem;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.45);
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .g-search-bar:focus-within {
    border-color: rgba(56, 189, 248, 0.35);
    box-shadow: 0 4px 40px rgba(0, 0, 0, 0.5), 0 0 0 3px rgba(56, 189, 248, 0.07);
  }

  .g-search-icon {
    width: 18px;
    height: 18px;
    color: #475569;
    flex-shrink: 0;
  }

  .g-search-bar input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: #f1f5f9;
    font-size: 1rem;
    font-family: inherit;
    padding: 0.55rem 0;
    min-width: 0;
  }

  .g-search-bar input::placeholder {
    color: #3d5068;
  }

  .g-search-submit {
    background: linear-gradient(135deg, #38bdf8 0%, #2563eb 100%);
    color: white;
    border: none;
    padding: 0.6rem 1.4rem;
    border-radius: 22px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: opacity 0.2s, transform 0.15s;
    font-family: inherit;
    letter-spacing: 0.02em;
  }

  .g-search-submit:hover {
    opacity: 0.88;
    transform: scale(1.02);
  }

  .g-platform-row {
    display: flex;
    align-items: center;
    padding: 0.55rem 1.4rem 0;
    gap: 0.4rem;
  }

  .g-platform-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    color: #475569;
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  .g-platform-badge::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #38bdf8;
    opacity: 0.7;
  }

  .g-action-row {
    animation: fadeInUp 0.7s ease-out 0.25s backwards;
  }

  .g-btn-secondary {
    background: none;
    border: none;
    color: #475569;
    font-size: 0.875rem;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: color 0.2s;
    font-family: inherit;
    font-weight: 500;
  }

  .g-btn-secondary:hover {
    color: #94a3b8;
  }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 付費牆 */
.lock-pill { background: rgba(148, 163, 184, 0.15); color: var(--text-dim); font-size: 0.8rem; font-weight: 800; padding: 0.2rem 0.6rem; border-radius: 2rem; margin-left: 0.4rem; white-space: nowrap; }
.lock-pill-inline { padding: 0.2rem 0.4rem; }
.lock-cell { cursor: pointer; }
.paywall-chart { position: relative; }
.paywall-placeholder { position: absolute; inset: 0; width: 100%; height: 100%; color: rgba(148, 163, 184, 0.25); filter: blur(3px); }
.paywall-overlay { position: relative; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 0.75rem; padding: 1rem; }
.paywall-title { font-size: 1.1rem; font-weight: 800; color: #fff; }
.paywall-desc { color: var(--text-dim); font-size: 0.9rem; line-height: 1.6; }
.paywall-note { margin-top: 0.75rem; font-size: 0.85rem; }
.paywall-modal-backdrop { position: fixed; inset: 0; background: rgba(2, 6, 23, 0.7); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.paywall-modal { position: relative; width: 100%; max-width: 440px; gap: 1rem; }
.paywall-modal h3 { color: #fff; font-size: 1.25rem; }
.paywall-close { position: absolute; top: 0.75rem; right: 1rem; background: none; border: none; color: var(--text-dim); font-size: 1.5rem; cursor: pointer; }
.paywall-input { font-size: 1rem; }
.paywall-consent { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.8rem; color: var(--text-dim); line-height: 1.5; cursor: pointer; }
.showcase-tag { background: var(--green); color: #000; }
.stale-locked { padding: 1.5rem 0.5rem; color: var(--text-dim); }
.stale-locked p { margin-bottom: 1rem; line-height: 1.6; }
.stale-scroll { max-height: 420px; overflow-y: auto; }
.compare-box { margin: 1.25rem 0; padding: 1rem; border-radius: 0.8rem; background: rgba(255,255,255,0.03); }
.compare-title { font-weight: 700; font-size: 0.9rem; margin-bottom: 0.9rem; }
.compare-title .text-dim { font-weight: 400; font-size: 0.8rem; }
.compare-bar { position: relative; height: 6px; border-radius: 3px; background: linear-gradient(90deg, var(--green), var(--orange), var(--red)); }
.compare-dot { position: absolute; top: 50%; width: 14px; height: 14px; border-radius: 50%; background: #fff; border: 3px solid var(--primary); transform: translate(-50%, -50%); }
.compare-scale { display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--text-dim); margin-top: 0.4rem; }
.compare-desc { font-size: 0.85rem; margin-top: 0.6rem; line-height: 1.6; }
.compare-deals { margin-top: 1rem; padding-top: 1rem; border-top: 1px solid rgba(255,255,255,0.06); }
.compare-deals:first-child { margin-top: 0; padding-top: 0; border-top: none; }
.deals-table { width: 100%; font-size: 0.78rem; margin-top: 0.5rem; color: var(--text-dim); }
.deals-table td { padding: 0.2rem 0.3rem 0.2rem 0; }
.compare-source { font-size: 0.7rem; color: var(--text-dim); margin-top: 0.6rem; opacity: 0.8; }
.cadence-note { color: var(--text-dim); font-size: 0.8rem; }
.relist-tag { background: var(--primary); }
.relist-badge { margin-top: 0.5rem; font-size: 0.85rem; color: var(--primary); font-weight: 700; }
.paywall-link { color: var(--primary); cursor: pointer; margin-left: 0.4rem; font-weight: 700; }
.paywall-blur-row td { color: var(--text-dim); }
.paywall-consent input { margin-top: 0.2rem; flex-shrink: 0; }

/* 物件頁延伸內容（docs/ENGAGEMENT_PRD.md） */
.breadcrumb { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; font-size: 0.85rem; color: var(--text-dim); margin-bottom: 1rem; }
.breadcrumb a { color: var(--primary); cursor: pointer; }
.breadcrumb .crumb-sep { opacity: 0.6; }
.breadcrumb .crumb-current { color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 12rem; }
.successor-banner { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.9rem 1rem; margin-bottom: 1rem; border-radius: 0.8rem; background: rgba(56, 189, 248, 0.1); color: var(--primary); font-weight: 700; font-size: 0.9rem; }
.status-line { margin-top: 0.5rem; font-size: 0.85rem; color: var(--green); font-weight: 700; }
.status-line.status-unlisted { color: var(--orange); }
.loc-chips { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
.loc-chip { font-size: 0.8rem; padding: 0.3rem 0.75rem; border-radius: 2rem; border: 1px solid var(--border); color: var(--text); cursor: pointer; background: rgba(255,255,255,0.03); }
.loc-chip:hover { border-color: var(--primary); color: var(--primary); }
.price-label { font-size: 0.8rem; color: var(--text-dim); text-align: right; }
.outbound-link { display: block; text-align: center; margin-top: 0.8rem; font-size: 0.85rem; color: var(--text-dim); cursor: pointer; text-decoration: underline; }
.related-card { margin-bottom: 2rem; }
.related-header { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 1.25rem; }
.seg-tabs { display: flex; gap: 0.4rem; }
.seg-tabs button { font-size: 0.8rem; padding: 0.35rem 0.8rem; border-radius: 2rem; border: 1px solid var(--border); background: transparent; color: var(--text-dim); cursor: pointer; }
.seg-tabs button.active { border-color: var(--primary); color: var(--primary); background: rgba(56, 189, 248, 0.1); }
.related-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; }
.related-item { padding: 1rem; border-radius: 1rem; border: 1px solid var(--border); background: rgba(255,255,255,0.02); cursor: pointer; transition: 0.2s; min-width: 0; }
.related-item:hover { border-color: var(--primary); }
.related-name { font-weight: 800; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.related-meta { font-size: 0.78rem; color: var(--text-dim); margin-top: 0.3rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.related-price { display: flex; align-items: center; justify-content: space-between; gap: 0.4rem; margin-top: 0.6rem; }
.related-price .price { color: var(--primary); font-weight: 800; }
.verdict-list { margin: 1.5rem 0 0; padding: 1rem 1.25rem 1rem 2.25rem; border-radius: 0.8rem; background: rgba(56, 189, 248, 0.06); border-left: 3px solid var(--primary); line-height: 1.8; font-size: 0.95rem; }
.spec-row { display: flex; flex-wrap: wrap; gap: 0.5rem 1.75rem; margin-top: 1.25rem; font-size: 0.95rem; font-weight: 700; color: #fff; }
.spec-row label { display: block; font-size: 0.72rem; font-weight: 400; color: var(--text-dim); margin-bottom: 0.15rem; }
.hero-cta { display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem 1.25rem; margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid var(--border); }
.hero-cta .outbound-link { margin-top: 0; }
.updated-note { margin-left: auto; font-size: 0.75rem; color: var(--text-dim); }
.card-title-row { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 0.5rem; margin-bottom: 1rem; }
.compare-card h3 { margin-bottom: 1rem; }
.trend-legend { display: flex; flex-wrap: wrap; gap: 0.4rem 1rem; margin-top: 0.75rem; font-size: 0.78rem; }
.legend-prev::before, .legend-cur::before { content: ''; display: inline-block; width: 1.25rem; margin-right: 0.35rem; vertical-align: middle; border-top: 3px dashed #94a3b8; }
.legend-cur::before { border-top: 3px solid var(--primary); }
.detail-toggle { margin-top: 1rem; align-self: flex-start; background: none; border: none; padding: 0; color: var(--primary); font-weight: 700; font-size: 0.85rem; cursor: pointer; }
.trend-table { margin-top: 0.75rem; }
.gap-row td { color: var(--primary); font-size: 0.8rem; font-weight: 700; background: rgba(56, 189, 248, 0.06); }
.prev-row td { color: var(--text-dim); }
.pulse-row { display: flex; flex-wrap: wrap; gap: 0.5rem; margin: -0.25rem 0 1.25rem; }
.pulse-row a { font-size: 0.8rem; padding: 0.3rem 0.75rem; border-radius: 2rem; border: 1px solid var(--border); color: var(--text-dim); cursor: pointer; }
.pulse-row a:hover { border-color: var(--primary); color: var(--primary); }
.pulse-row b { color: var(--primary); margin-left: 0.2rem; }
.chart-empty { display: flex; flex-direction: column; justify-content: center; gap: 0.5rem; min-height: 8rem; font-size: 0.95rem; line-height: 1.6; }
.notfound-card { text-align: center; padding: 3rem 1.5rem; gap: 1rem; align-items: center; }
.notfound-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; justify-content: center; }
@media (max-width: 767px) {
  .related-grid { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 0.75rem; padding-bottom: 0.25rem; }
  .related-item { flex: 0 0 72%; scroll-snap-align: start; }
}
</style>
